/**
 * Contacts Page
 */
import EmptyState from '@/components/ui/EmptyState';
import PageHeader from '@/components/ui/PageHeader';
import StatusChip from '@/components/ui/StatusChip';
import UsageChip from '@/components/common/UsageChip';
import { refreshUsage } from '@/hooks/useUsageLimits';
import { usePermissionAccess } from '@/hooks/usePermissionAccess';
import { useAuth } from '@/hooks/useAuth';
import SearchBar from '@/components/ui/SearchBar';
import PhoneField, { formatPhoneDisplay, isValidPhone } from '@/components/ui/PhoneField';
import adminApi, {
  ContactAuditEvent,
  ContactBulkSelection,
  ContactCustomAttribute,
  ContactImportAssignment,
  ContactImportResult,
  FieldDefinition,
  Segment,
  SegmentFilterParams as ApiSegmentFilterParams,
  TeamMember,
} from '@/services/admin.api';
import ContactAssignmentRulesDialog from './ContactAssignmentRulesDialog';
import DynamicFields from '@/components/common/DynamicFields';
import { showPlanLockIfTagged } from '@/utils/plan-lock.util';
import { ADVANCED_FILTER_OPTIONS } from '@/constants/advancedContactFilters';
import { useCachedResource } from '@/hooks/admin/useCachedResource';
import { useListViewScroll } from '@/hooks/admin/useListViewScroll';
import { resolveUpdate, Updater, useUrlListState } from '@/hooks/admin/useUrlListState';
import {
  ASSIGNED_TO_ALL,
  ASSIGNED_TO_UNASSIGNED,
  AttributeConditionDraft,
  CONTACTS_URL_SPEC,
  emptySegmentFilters,
  pickSegmentFilters,
  SegmentFilters,
  segmentParamsToFilters,
  spreadSegmentFilters,
} from './Contacts.urlState';

// The filter vocabulary now lives beside its URL codec; re-exported here so the
// existing importers (Contacts.filters.test.ts, campaign targeting) are unmoved.
export { ASSIGNED_TO_ALL, ASSIGNED_TO_UNASSIGNED, emptySegmentFilters };
export type { AttributeConditionDraft, SegmentFilters };
import {
  Add,
  Api,
  Archive as ArchiveIcon,
  AssignmentInd,
  Block,
  Campaign,
  CheckCircleOutline,
  Close,
  Contacts as ContactsIcon,
  DeleteOutline,
  Edit as EditIcon,
  PersonAddAlt1,
  PersonRemove,
  ErrorOutline,
  ExpandLess,
  ExpandMore,
  FilterList,
  Forum,
  History as HistoryIcon,
  InfoOutlined,
  Inventory2,
  LocalOffer,
  MoreVert,
  Schedule,
  Send,
  Settings,
  TrendingUp,
  Tune,
  UploadFile,
  Visibility,
} from '@mui/icons-material';
import {
  Alert,
  Autocomplete,
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Checkbox,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  InputLabel,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Pagination,
  Select,
  Snackbar,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  alpha,
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';

// ─── CSV import helpers ─────────────────────────────────────────────────────

const splitContactsCsvLine = (line: string): string[] => {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    const next = i + 1 < line.length ? line[i + 1] : '';

    if (char === '"') {
      if (inQuotes && next === '"') {
        current += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }

    if (char === ',' && !inQuotes) {
      result.push(current.trim());
      current = '';
      continue;
    }

    current += char;
  }

  result.push(current.trim());
  return result;
};

const normalizeContactHeader = (header: string): string =>
  header
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '');

const CONTACT_FIELD_ALIASES: Record<string, string> = {
  name: 'name',
  fullname: 'name',
  customername: 'name',
  contactname: 'name',
  phone: 'phone',
  phonenumber: 'phone',
  mobile: 'phone',
  customerphone: 'phone',
  whatsapp: 'phone',
  whatsappnumber: 'phone',
  email: 'email',
  emailaddress: 'email',
  customeremail: 'email',
  country: 'countryCode',
  countrycode: 'countryCode',
  customercountrycode: 'countryCode',
  tags: 'tags',
  tag: 'tags',
  segment: 'tags',
  // Only the two unambiguous spellings are auto-detected. "remarks",
  // "comments", "description" and friends are deliberately NOT here: silently
  // turning a free-text column into notes changes what the contact records,
  // and the mapping dropdown is one click away.
  note: 'notes',
  notes: 'notes',
  // No owner/assignee aliases either, and for a stronger reason — auto-mapping
  // one would turn an ordinary re-import into a mass reassignment. Choosing it
  // has to be a decision, and it only takes effect in 'column' mode anyway.
};

const toContactFieldMapping = (headers: string[]): Record<string, string> => {
  const mapping: Record<string, string> = {};
  for (const header of headers) {
    const normalized = normalizeContactHeader(header);
    if (CONTACT_FIELD_ALIASES[normalized]) {
      mapping[header] = CONTACT_FIELD_ALIASES[normalized];
    }
  }
  return mapping;
};

const parseContactsCsvFile = async (
  file: File
): Promise<{ rows: Record<string, string>[]; fieldMapping: Record<string, string> }> => {
  const text = await file.text();
  const lines = text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  if (lines.length < 2) {
    return { rows: [], fieldMapping: {} };
  }

  const [firstLine] = lines;
  if (!firstLine) {
    return { rows: [], fieldMapping: {} };
  }

  const headers = splitContactsCsvLine(firstLine);
  const rows: Record<string, string>[] = lines.slice(1).map((line) => {
    const values = splitContactsCsvLine(line);
    const row: Record<string, string> = {};
    headers.forEach((header, index) => {
      row[header] = values[index] ?? '';
    });
    return row;
  });

  return { rows, fieldMapping: toContactFieldMapping(headers) };
};

/**
 * Extracts one sheet of an already-read .xlsx/.xls workbook into the same
 * `{ rows, fieldMapping }` shape the CSV path produces, so the rest of the
 * import flow (mapping UI, backend payload) is format-agnostic. `raw: false`
 * makes SheetJS return each cell's *displayed* string (e.g. a phone typed as
 * "+91 9633 478777" stays text), matching what the user sees in Excel; blank
 * rows are dropped so trailing empty rows don't become phantom contacts.
 */
const parseContactsExcelSheet = (
  workbook: XLSX.WorkBook,
  sheetName: string
): { rows: Record<string, string>[]; fieldMapping: Record<string, string> } => {
  const sheet = workbook.Sheets[sheetName];
  if (!sheet) return { rows: [], fieldMapping: {} };

  const aoa = XLSX.utils.sheet_to_json<Array<string | number | null>>(sheet, {
    header: 1,
    defval: '',
    raw: false,
    blankrows: false,
  });
  if (aoa.length < 2) return { rows: [], fieldMapping: {} };

  const headers = (aoa[0] || []).map((h) => String(h ?? '').trim());
  const rows: Record<string, string>[] = aoa
    .slice(1)
    .map((values) => {
      const row: Record<string, string> = {};
      headers.forEach((header, index) => {
        const cell = (values as Array<string | number | null>)[index];
        row[header] = cell == null ? '' : String(cell).trim();
      });
      return row;
    })
    .filter((row) => Object.values(row).some((v) => v !== ''));

  return { rows, fieldMapping: toContactFieldMapping(headers) };
};

/** Reads an .xlsx/.xls file into a workbook so its sheet names can be listed before picking one. */
const readContactsWorkbook = async (file: File): Promise<XLSX.WorkBook> => {
  const buffer = await file.arrayBuffer();
  return XLSX.read(buffer, { type: 'array' });
};

const CONTACT_CSV_TEMPLATE =
  'name,phone,email,countryCode,tags\nJohn Doe,+919876543210,john@example.com,IN,campaign;vip\n';

/**
 * CSV-import wire convention: a fieldMapping value of "custom:<key>" routes
 * that column into customAttributes.<key> instead of a fixed contact field.
 * Mirrors CUSTOM_FIELD_MAPPING_PREFIX in the backend's contact.service.ts
 * (no shared types package between the two apps).
 */
const CUSTOM_FIELD_MAPPING_PREFIX = 'custom:';
const CUSTOM_FIELD_SELECT_VALUE = 'custom';

/** Wire value for a column carrying free text to record as a contact note.
 *  Several columns may use it — each becomes its own note. Mirrors
 *  NOTE_FIELD_MAPPING_TARGET in the backend's contact.service.ts. */
const NOTE_FIELD_MAPPING_TARGET = 'notes';

/** Wire value for a column carrying the owning team member's e-mail address.
 *  Only honoured when the import runs in assignment mode 'column'. Mirrors
 *  ASSIGNEE_FIELD_MAPPING_TARGET in the backend's contact.service.ts. */
const ASSIGNEE_FIELD_MAPPING_TARGET = 'assignedTo';

const IMPORT_TARGET_FIELD_OPTIONS = [
  { value: '', label: "Don't import" },
  { value: 'name', label: 'Name' },
  { value: 'phone', label: 'Phone' },
  { value: 'email', label: 'Email' },
  { value: 'countryCode', label: 'Country Code' },
  { value: 'tags', label: 'Tags' },
  { value: NOTE_FIELD_MAPPING_TARGET, label: 'Note' },
  { value: ASSIGNEE_FIELD_MAPPING_TARGET, label: 'Assigned to (owner email)' },
  { value: CUSTOM_FIELD_SELECT_VALUE, label: 'Custom field…' },
];

/** Fallback country for phone numbers with no country code in the file (e.g. "09811353198")
 *  and no mapped Country Code column — mirrors CAMPAIGN_TIMEZONES' "India-heavy tenant base"
 *  default. Values are ISO 3166-1 alpha-2, same as the countryCode column convention. */
const IMPORT_DEFAULT_COUNTRY_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'IN', label: 'India' },
  { value: 'AE', label: 'UAE' },
  { value: 'GB', label: 'United Kingdom' },
  { value: 'US', label: 'United States' },
  { value: 'SA', label: 'Saudi Arabia' },
  { value: 'SG', label: 'Singapore' },
];

function emptyContactForm() {
  return {
    name: '',
    phone: '',
    email: '',
    countryCode: '',
    tags: [] as string[],
    customAttributes: [] as Array<{ key: string; value: string }>,
  };
}

/** Trims, drops empties, and dedupes case-insensitively (first-seen casing wins). */
function cleanTags(rawTags: string[]): string[] {
  const seen = new Set<string>();
  const cleaned: string[] = [];
  for (const raw of rawTags) {
    const trimmed = String(raw).trim();
    if (!trimmed) continue;
    const key = trimmed.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    cleaned.push(trimmed);
  }
  return cleaned;
}

/** Custom-attribute keys can be reused across many contacts; collisions are only invalid within a single contact's own form. */
const MAX_CUSTOM_FIELDS_PER_CONTACT = 50;

interface ContactExternalAttribute {
  clientId: string;
  clientName: string;
  attributes: Array<{ key: string; value: any }>;
  tags?: string[];
  externalContactId?: string;
  lastSyncedAt?: string;
}

interface Contact {
  _id: string;
  customerName?: string;
  customerPhone: string;
  customerEmail?: string;
  customerCountry?: string;
  customerCountryCode?: string;
  tags?: string[];
  customAttributes?: ContactCustomAttribute[];
  lastMessage?: string;
  messageCount?: number;
  lastMessageAt?: string;
  notContacted?: boolean;
  noteCount?: number;
  lastNote?: { text: string; authorName: string; createdAt?: string | null } | null;
  assigneeSeenAt?: string;
  lastContactAt?: string | null;
  routing?: { assignedAt?: string; state?: string };
  status: 'active' | 'archived' | 'resolved' | 'spam';
  assignedTo?: {
    _id?: string;
    firstName?: string;
    lastName?: string;
    email?: string;
  };
  source?: string;
  /** Drill-down for `source` — e.g. the external client's name for source==='api'. */
  sourceDetail?: string;
  externalAttributes?: ContactExternalAttribute[];
}

const CONTACTS_PAGE_SIZE = 20;

/** Frozen empties shared by the cached reference lists: a fresh `[]` per render
 *  would hand each hook a new fallback identity on every pass. */
const EMPTY_STRINGS: string[] = [];
const NO_TEAM_MEMBERS: TeamMember[] = [];
const NO_SEGMENTS: Segment[] = [];
const NO_CAMPAIGNS: CampaignOption[] = [];

/** The list request: the shared segment params, plus the list-only keys and the
 *  two fields the query string wants flattened (`tags`, `attributes`). */
type ContactListParams = Omit<ApiSegmentFilterParams, 'tags' | 'attributes'> & {
  page: number;
  limit: number;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  search?: string;
  contacted?: 'yes' | 'no';
  tags?: string;
  attributes?: string;
};
const AUDIT_PAGE_SIZE = 25;

/** Chip colour per audit action — deletes must be unmistakable in a list that
 *  is mostly benign 'updated' rows. */
/** Renders one side of an audit `changedKeys` diff. Mirrors saas-front's own
 *  formatter (TenantDetail.tsx) so the same event reads identically in the
 *  tenant app and the super-admin panel. Tag arrays are the common case here,
 *  so objects/arrays are JSON'd and truncated rather than dropped. */
function formatAuditValue(value: unknown): string {
  if (value === undefined || value === null) return '—';
  if (typeof value === 'object') {
    const json = JSON.stringify(value);
    return json.length > 60 ? `${json.slice(0, 57)}…` : json;
  }
  return String(value);
}

/** Icon-only header actions. Mirrors the outlined Button shell (radius 8,
 *  #e2e8f0 border, primary-tinted hover) so an icon button and a labelled
 *  button read as one control family rather than two unrelated widgets. */
const HEADER_ICON_BUTTON_SX = {
  width: 32,
  height: 32,
  borderRadius: 2,
  border: '1px solid #e2e8f0',
  color: 'text.secondary',
  transition: 'all 0.15s ease',
  '&:hover': {
    borderColor: '#4f46e5',
    color: '#4f46e5',
    bgcolor: alpha('#4f46e5', 0.04),
  },
} as const;

/** Audit actions map onto StatusChip's shared tonal palette (soft tint + dark
 *  text) — the same treatment every other list in the app uses. They previously
 *  used raw MUI `color=` chips, which render fully saturated fills; a wall of
 *  solid red/blue/orange pills read as an alert state rather than a log. */
const AUDIT_ACTION_STATUS: Record<string, string> = {
  created: 'success',
  updated: 'info',
  archived: 'warning',
  deleted: 'error',
};

const STATUS_FILTER_OPTIONS = [
  { value: 'all', label: 'All statuses' },
  { value: 'active', label: 'Active' },
  { value: 'archived', label: 'Archived' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'spam', label: 'Spam' },
];

interface CampaignOption {
  _id: string;
  name: string;
}

/** Tier 2 segment filters. Values must match the backend's contact-segment-filter.service.ts. */
const SOURCE_OPTIONS = [
  { value: 'all', label: 'Any source' },
  { value: 'manual', label: 'Manual' },
  { value: 'csvImport', label: 'CSV import' },
  { value: 'api', label: 'API' },
  { value: 'inbound', label: 'Inbound (customer-initiated)' },
  { value: 'campaign', label: 'Campaign' },
  { value: 'preAdded', label: 'Pre-added (before source tracking)' },
];

/** Source is provenance (how a row entered the system), not a category the
 *  user assigns — so unlike Tags it's rendered as icon + plain text rather
 *  than a chip, the same visual distinction CRMs like Salesforce/HubSpot draw
 *  between a "Lead Source" column and user-defined tags. */
const SOURCE_META: Record<string, { label: string; icon: React.ReactNode }> = {
  manual: { label: 'Manual', icon: <PersonAddAlt1 sx={{ fontSize: 16 }} /> },
  csvImport: { label: 'CSV import', icon: <UploadFile sx={{ fontSize: 16 }} /> },
  api: { label: 'API', icon: <Api sx={{ fontSize: 16 }} /> },
  inbound: { label: 'Inbound', icon: <Forum sx={{ fontSize: 16 }} /> },
  campaign: { label: 'Campaign', icon: <Campaign sx={{ fontSize: 16 }} /> },
  preAdded: { label: 'Pre-added', icon: <Inventory2 sx={{ fontSize: 16 }} /> },
};

/** Local-time YYYY-MM-DD (the shape `<input type="date">` reads/writes), `days`
 *  before today — 0 = today. Built from local calendar parts, not toISOString,
 *  so the preset a user picks means their own day rather than a UTC-shifted one. */
function isoDaysAgo(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Options for the compact date-range dropdown shared by every date filter in the
 *  drawer (Date added, Last message, Last note added, Campaign sent). "Custom
 *  range" is the only one that reveals the From/To inputs — the rolling presets
 *  cover the overwhelmingly common asks in a single click, which is what keeps the
 *  drawer from being a wall of near-identical date boxes. */
const DATE_RANGE_OPTIONS = [
  { value: 'all', label: 'Any time' },
  { value: 'today', label: 'Today' },
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: 'custom', label: 'Custom range' },
];

/** Which dropdown option a stored {from,to} pair corresponds to, so the control
 *  shows the right label after a reload / segment load without storing a separate
 *  "which preset" flag. A preset only matches while its rolling `from` still equals
 *  today's computed bound; once that day passes the same absolute date reads as a
 *  "Custom range", which is exactly what it now is. */
function matchDateRangePreset(from: string, to: string): string {
  if (!from && !to) return 'all';
  if (!to) {
    if (from === isoDaysAgo(0)) return 'today';
    if (from === isoDaysAgo(7)) return '7d';
    if (from === isoDaysAgo(30)) return '30d';
  }
  return 'custom';
}

/**
 * One compact date-range control: a single-line preset dropdown that expands to
 * From/To inputs only for "Custom range". Reused by all four drawer date filters
 * so they read as one consistent control instead of four hand-rolled stacks.
 */
function DateRangeFilter({
  label,
  helper,
  from,
  to,
  onChange,
}: {
  label: string;
  helper?: string;
  from: string;
  to: string;
  onChange: (next: { from: string; to: string }) => void;
}) {
  // Local UI-only latch: lets the user drop into "Custom range" before typing any
  // date. Without it, an empty custom range would derive back to "Any time" and
  // the inputs could never appear.
  const [customLatched, setCustomLatched] = React.useState(false);
  const derived = matchDateRangePreset(from, to);
  const preset = customLatched && derived === 'all' ? 'custom' : derived;
  const showCustom = preset === 'custom';

  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
        {label}
      </Typography>
      <FormControl size="small" fullWidth>
        <Select
          value={preset}
          onChange={(e) => {
            const v = String(e.target.value);
            if (v === 'custom') {
              setCustomLatched(true);
              return; // keep whatever from/to is there; reveal the inputs
            }
            setCustomLatched(false);
            if (v === 'all') onChange({ from: '', to: '' });
            else if (v === 'today') onChange({ from: isoDaysAgo(0), to: '' });
            else if (v === '7d') onChange({ from: isoDaysAgo(7), to: '' });
            else if (v === '30d') onChange({ from: isoDaysAgo(30), to: '' });
          }}
        >
          {DATE_RANGE_OPTIONS.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {showCustom && (
        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
          <TextField
            size="small"
            type="date"
            label="From"
            InputLabelProps={{ shrink: true }}
            value={from}
            onChange={(e) => onChange({ from: e.target.value, to })}
            fullWidth
          />
          <TextField
            size="small"
            type="date"
            label="To"
            InputLabelProps={{ shrink: true }}
            value={to}
            onChange={(e) => onChange({ from, to: e.target.value })}
            fullWidth
          />
        </Stack>
      )}
      {helper && (
        <Typography
          variant="caption"
          color="text.disabled"
          sx={{ display: 'block', mt: 0.5, fontSize: 11 }}
        >
          {helper}
        </Typography>
      )}
    </Box>
  );
}

const ENGAGEMENT_TIER_OPTIONS = [
  { value: 'all', label: 'Any engagement' },
  { value: 'active', label: 'Active (replied within 7 days)' },
  { value: 'atRisk', label: 'At-risk (7–30 days since reply)' },
  { value: 'dormant', label: 'Dormant (30+ days since reply)' },
  { value: 'neverEngaged', label: 'Never engaged (no reply yet)' },
];


/**
 * The complete contact-selection spec: every filter dimension the list is
 * currently showing, in the flat shape the backend's shared
 * `buildContactSelectionConditions` reads.
 *
 * Both the list fetch AND "select all matching" bulk actions go through this
 * one function, so the rows on screen and the rows a bulk action touches are
 * described by the same object. Previously the list built its params inline
 * while bulk sent a hand-picked tags/status/search subset, which is why bulk
 * had to be hidden whenever any other filter was active.
 *
 * Arrays stay arrays here (`tags`, `attributes`); `toQueryParams` flattens them
 * for the GET list, while the JSON bulk endpoints take them as-is — a tag
 * containing a comma survives the second path and not the first.
 */
export type ContactSelectionSpec = {
  search?: string;
  status?: string;
  tags?: string[];
  contacted?: 'yes' | 'no';
  /** 'yes' = has at least one note; 'no' = none. Independent of `contacted`. */
  notes?: 'yes' | 'no';
  advanced?: string;
  errorCode?: number;
  campaignId?: string;
  contactSource?: string;
  dateAddedFrom?: string;
  dateAddedTo?: string;
  engagementTier?: string;
  lastMessageFrom?: string;
  lastMessageTo?: string;
  lastNoteFrom?: string;
  lastNoteTo?: string;
  frequencyMin?: number;
  frequencyMax?: number;
  attributes?: Array<{ key: string; value: string }>;
  matchMode?: 'all' | 'any';
  campaignSentFrom?: string;
  campaignSentTo?: string;
  assignedTo?: string;
  minFailureRate?: number;
  minAttempts?: number;
};

export function buildContactSelectionSpec(args: {
  search: string;
  statusFilter: string;
  tagFilter: string[];
  contactedFilter: string;
  notesFilter?: string;
  filters: SegmentFilters;
}): ContactSelectionSpec {
  const { search, statusFilter, tagFilter, contactedFilter, notesFilter, filters: f } = args;
  const spec: ContactSelectionSpec = {};

  if (search) spec.search = search;
  if (statusFilter !== 'all') spec.status = statusFilter;
  if (tagFilter.length > 0) spec.tags = tagFilter;
  if (contactedFilter === 'yes' || contactedFilter === 'no') spec.contacted = contactedFilter;
  if (notesFilter === 'yes' || notesFilter === 'no') spec.notes = notesFilter;

  // Filters with a required sub-parameter (an error code / a chosen campaign)
  // are only sent once that sub-parameter is actually filled in — otherwise the
  // backend 400s, and until then this should behave as "no filter", not an
  // error. This is also what keeps results authentic: never send a filter key
  // whose scoping value hasn't been chosen yet.
  if (f.advancedFilter === 'failedByError') {
    const code = parseInt(f.errorCode.trim(), 10);
    if (Number.isInteger(code)) {
      spec.advanced = f.advancedFilter;
      spec.errorCode = code;
    }
  } else if (f.advancedFilter === 'neverDeliveredThisCampaign') {
    if (f.campaignFilter) {
      spec.advanced = f.advancedFilter;
      spec.campaignId = f.campaignFilter;
    }
  } else if (f.advancedFilter !== 'all') {
    spec.advanced = f.advancedFilter;
  }

  if (f.source !== 'all') spec.contactSource = f.source;
  if (f.dateAddedFrom) spec.dateAddedFrom = f.dateAddedFrom;
  if (f.dateAddedTo) spec.dateAddedTo = f.dateAddedTo;
  if (f.engagementTier !== 'all') spec.engagementTier = f.engagementTier;
  if (f.lastMessageFrom) spec.lastMessageFrom = f.lastMessageFrom;
  if (f.lastMessageTo) spec.lastMessageTo = f.lastMessageTo;
  if (f.lastNoteFrom) spec.lastNoteFrom = f.lastNoteFrom;
  if (f.lastNoteTo) spec.lastNoteTo = f.lastNoteTo;
  if (f.frequencyMin) {
    const n = parseInt(f.frequencyMin, 10);
    if (Number.isInteger(n) && n >= 0) spec.frequencyMin = n;
  }
  if (f.frequencyMax) {
    const n = parseInt(f.frequencyMax, 10);
    if (Number.isInteger(n) && n >= 0) spec.frequencyMax = n;
  }
  if (f.campaignSentFrom) spec.campaignSentFrom = f.campaignSentFrom;
  if (f.campaignSentTo) spec.campaignSentTo = f.campaignSentTo;

  const validAttributes = f.attributes
    .map((a) => ({ key: a.key.trim(), value: a.value.trim() }))
    .filter((a) => a.key && a.value);
  if (validAttributes.length > 0) spec.attributes = validAttributes;
  // matchMode only matters (and is only sent) once there's more than one
  // condition to combine — a single tag with no attributes behaves identically
  // either way.
  if (tagFilter.length + validAttributes.length > 1) spec.matchMode = f.matchMode;

  if (f.assignedTo !== ASSIGNED_TO_ALL) spec.assignedTo = f.assignedTo;
  if (f.minFailureRate) {
    const n = parseFloat(f.minFailureRate);
    if (Number.isFinite(n)) {
      spec.minFailureRate = n;
      // minAttempts only means anything alongside minFailureRate — omit otherwise.
      if (f.minAttempts) {
        const attempts = parseInt(f.minAttempts, 10);
        if (Number.isInteger(attempts) && attempts >= 1) spec.minAttempts = attempts;
      }
    }
  }

  return spec;
}

/** Flattens a spec for the GET list endpoint, where arrays can't travel as-is. */
export function toQueryParams(spec: ContactSelectionSpec): Record<string, unknown> {
  const { tags, attributes, ...rest } = spec;
  return {
    ...rest,
    ...(tags ? { tags: tags.join(',') } : {}),
    ...(attributes ? { attributes: JSON.stringify(attributes) } : {}),
  };
}

/** True when the spec restricts anything at all. A spec that narrows nothing
 *  selects the entire account, which every bulk action refuses (the backend
 *  enforces the same rule against the RESOLVED conditions). */
export function specHasAnyFilter(spec: ContactSelectionSpec): boolean {
  return Object.keys(spec).some((k) => k !== 'matchMode');
}


/** True (per field) if the filter is actually set enough to be sent — a filter picked
 *  but missing its required sub-value (e.g. "Failed by error code" with no code typed
 *  yet) isn't really active. Mirrors buildFilterParams' own "don't send it yet" logic
 *  exactly, so the chip row / badge count / bulk-select guard never disagree with
 *  what's actually being sent to the API. */
export function isAdvancedFilterSet(f: SegmentFilters): boolean {
  if (f.advancedFilter === 'failedByError')
    return Number.isInteger(parseInt(f.errorCode.trim(), 10));
  if (f.advancedFilter === 'neverDeliveredThisCampaign') return Boolean(f.campaignFilter);
  return f.advancedFilter !== 'all';
}

/**
 * Converts committed SegmentFilters into the flat `ISegmentFilterParams` shape the
 * backend Segment model stores and the `getConversations` API accepts — the single
 * source of truth "Save as segment", "Update segment", and the live fetch all
 * translate through, so nothing can ever drift from what a filter combination
 * actually means.
 */
export function toSegmentFilterParamsPayload(args: {
  statusFilter: string;
  tagFilter: string[];
  segmentFilters: SegmentFilters;
}): ApiSegmentFilterParams {
  const { statusFilter, tagFilter, segmentFilters: f } = args;
  const payload: ApiSegmentFilterParams = {};

  if (statusFilter !== 'all') payload.status = statusFilter;
  if (tagFilter.length > 0) payload.tags = tagFilter;
  if (f.advancedFilter === 'failedByError') {
    const code = parseInt(f.errorCode.trim(), 10);
    if (Number.isInteger(code)) {
      payload.advanced = f.advancedFilter;
      payload.errorCode = code;
    }
  } else if (f.advancedFilter === 'neverDeliveredThisCampaign') {
    if (f.campaignFilter) {
      payload.advanced = f.advancedFilter;
      payload.campaignId = f.campaignFilter;
    }
  } else if (f.advancedFilter !== 'all') {
    payload.advanced = f.advancedFilter;
  }
  if (f.source !== 'all') payload.contactSource = f.source;
  if (f.dateAddedFrom) payload.dateAddedFrom = f.dateAddedFrom;
  if (f.dateAddedTo) payload.dateAddedTo = f.dateAddedTo;
  if (f.engagementTier !== 'all') payload.engagementTier = f.engagementTier;
  if (f.lastMessageFrom) payload.lastMessageFrom = f.lastMessageFrom;
  if (f.lastMessageTo) payload.lastMessageTo = f.lastMessageTo;
  if (f.lastNoteFrom) payload.lastNoteFrom = f.lastNoteFrom;
  if (f.lastNoteTo) payload.lastNoteTo = f.lastNoteTo;
  if (f.frequencyMin) {
    const n = parseInt(f.frequencyMin, 10);
    if (Number.isInteger(n) && n >= 0) payload.frequencyMin = n;
  }
  if (f.frequencyMax) {
    const n = parseInt(f.frequencyMax, 10);
    if (Number.isInteger(n) && n >= 0) payload.frequencyMax = n;
  }
  if (f.campaignSentFrom) payload.campaignSentFrom = f.campaignSentFrom;
  if (f.campaignSentTo) payload.campaignSentTo = f.campaignSentTo;
  const validAttributes = f.attributes
    .map((a) => ({ key: a.key.trim(), value: a.value.trim() }))
    .filter((a) => a.key && a.value);
  if (validAttributes.length > 0) payload.attributes = validAttributes;
  if (tagFilter.length + validAttributes.length > 1) payload.matchMode = f.matchMode;
  if (f.assignedTo !== ASSIGNED_TO_ALL) payload.assignedTo = f.assignedTo;
  if (f.minFailureRate) {
    const n = parseFloat(f.minFailureRate);
    if (Number.isFinite(n)) payload.minFailureRate = n;
  }
  if (f.minAttempts) {
    const n = parseInt(f.minAttempts, 10);
    if (Number.isInteger(n) && n >= 1) payload.minAttempts = n;
  }

  return payload;
}

/** One labeled group inside the Filters drawer — gives each dimension a visual
 *  category instead of a flat, undifferentiated stack of controls. */
const FilterSection: React.FC<{
  title: string;
  icon: React.ReactNode;
  helper?: string;
  /** Sections that already have a filter set (as of when the drawer was opened)
   *  start expanded so reopening the drawer never hides an active filter; every
   *  other section starts collapsed to keep the drawer scannable. Only read once
   *  on mount (the drawer unmounts its contents on close, so this re-evaluates
   *  correctly each time it's reopened) — collapsing an active section afterward
   *  must not snap back open on the next render. */
  defaultExpanded?: boolean;
  children: React.ReactNode;
}> = ({ title, icon, helper, defaultExpanded, children }) => {
  const [expanded, setExpanded] = useState(Boolean(defaultExpanded));
  return (
    <Box>
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        onClick={() => setExpanded((e) => !e)}
        sx={{ cursor: 'pointer', userSelect: 'none' }}
      >
        <Box sx={{ display: 'flex', color: 'primary.main' }}>{icon}</Box>
        <Typography variant="subtitle2" fontWeight={700} sx={{ flex: 1 }}>
          {title}
        </Typography>
        {helper && (
          <Tooltip title={helper} arrow placement="top">
            <InfoOutlined
              sx={{ fontSize: 15, color: 'text.disabled', cursor: 'help' }}
              onClick={(e) => e.stopPropagation()}
            />
          </Tooltip>
        )}
        <IconButton
          size="small"
          sx={{ p: 0.25 }}
          aria-label={expanded ? 'Collapse section' : 'Expand section'}
        >
          {expanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
        </IconButton>
      </Stack>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Stack spacing={2} sx={{ mt: 1.5 }}>
          {children}
        </Stack>
      </Collapse>
    </Box>
  );
};

const Contacts: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // The committed query — filters, search and page — lives in the URL rather
  // than in component state. Opening a contact unmounts this page, so anything
  // held in `useState` was gone by the time you came back; the URL survives it,
  // makes a filtered view shareable, and (because the first render already holds
  // the parsed query) costs exactly one fetch on mount, deep links included.
  const { state: query, patch: patchQuery } = useUrlListState(CONTACTS_URL_SPEC);

  const [contacts, setContacts] = useState<Contact[]>([]);
  // The text input keeps local state so typing stays instant; only the debounced
  // value is committed to the query, and therefore only it reaches the API.
  const [search, setSearch] = useState(query.search);
  const debouncedSearch = query.search;

  const statusFilter = query.status;
  const setStatusFilter = (value: string) => patchQuery({ status: value });
  // 'no' = never messaged AND no notes (backend ?contacted param); such
  // contacts never auto-enter the lead pipeline — this filter is how the team
  // works the untouched list.
  // Read via the touch-state segmented toggle below (setTouchFilterValue),
  // which patches both params together — no standalone setter for either.
  const contactedFilter = query.contacted;
  /** Independent of contactedFilter: isolates contacts worked by note alone. */
  const notesFilter = query.notes;
  const tagFilter = query.tags;
  const setTagFilter = (update: Updater<string[]>) =>
    patchQuery({ tags: resolveUpdate(update, query.tags) });
  /** Touch-state split for the CURRENT filter set — how many of the contacts
   *  on screen have actually been worked (a message exchanged or a note
   *  recorded) versus sitting untouched. Fetched alongside the list from an
   *  endpoint that resolves the same filters through the same query builder,
   *  so the numbers always describe these rows. */
  const [touchSummary, setTouchSummary] = useState<{
    total: number;
    contacted: number;
    notContacted: number;
    withNotes: number;
    messaged: number;
  } | null>(null);
  // Every filter beyond search/status/tags — Tier 1 (WhatsApp delivery status) through
  // Tier 3 (assignment, quality risk) — lives in ONE unified shape now, with ONE
  // interaction model: `filters` is the applied/committed state (drives fetchContacts);
  // `filterDraft` is edited freely inside the Filters drawer and only takes effect on
  // "Apply". Search/status/tags stay live (no Apply gate) since they're single-value,
  // instant-feedback controls — the drawer is for the filters dense enough to need staging.
  // The grouped view of the same fields the URL holds flat. Memoised so its
  // identity only moves when the query really changes — that is what keeps it
  // safe to use directly as the fetch effect's dependency.
  const filters = useMemo(() => pickSegmentFilters(query), [query]);
  const setFilters = (update: Updater<SegmentFilters>) =>
    patchQuery(spreadSegmentFilters(resolveUpdate(update, filters)));
  const [filterDraft, setFilterDraft] = useState<SegmentFilters>(() => pickSegmentFilters(query));
  const [filtersDrawerOpen, setFiltersDrawerOpen] = useState(false);

  // ── Saved dynamic segments (Tier 3) ─────────────────────────────────────
  const [segmentsMenuAnchor, setSegmentsMenuAnchor] = useState<HTMLElement | null>(null);
  const [saveSegmentOpen, setSaveSegmentOpen] = useState(false);
  const [saveSegmentName, setSaveSegmentName] = useState('');
  const [saveSegmentDescription, setSaveSegmentDescription] = useState('');
  const [saveSegmentSaving, setSaveSegmentSaving] = useState(false);
  const [saveSegmentError, setSaveSegmentError] = useState('');
  const [segmentDeleteTarget, setSegmentDeleteTarget] = useState<Segment | null>(null);
  const [segmentActionError, setSegmentActionError] = useState('');
  // Edit (rename / optionally re-point filters) an existing saved segment.
  const [editSegmentTarget, setEditSegmentTarget] = useState<Segment | null>(null);
  const [editSegmentName, setEditSegmentName] = useState('');
  const [editSegmentDescription, setEditSegmentDescription] = useState('');
  // Off by default — renaming a segment must never silently redefine what it
  // filters; the admin has to explicitly opt into overwriting filterParams too.
  const [editSegmentUpdateFilters, setEditSegmentUpdateFilters] = useState(false);
  const [editSegmentSaving, setEditSegmentSaving] = useState(false);
  const [editSegmentError, setEditSegmentError] = useState('');

  const page = query.page;
  const setPage = (value: number) => patchQuery({ page: value });

  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);

  // Filters and page come back from the URL; the scroll offset comes back from
  // here, so returning from a contact lands on the row you opened rather than at
  // the top of page 14. Gated on rows existing — the document is too short to
  // scroll until they render.
  useListViewScroll(!loading && contacts.length > 0);
  const [attributesOpen, setAttributesOpen] = useState(false);
  const [visibleFields, setVisibleFields] = useState({
    email: true,
    country: true,
    tags: true,
    source: true,
    customFields: true,
    // Off by default: the wide preview text made the table overflow the
    // viewport; the compact Date column below carries the when, and the full
    // conversation is one row-click away. Re-enable via Table Columns.
    conversation: false,
    date: true,
    lastNote: true,
    status: true,
    owner: true,
  });

  // ── Add / Edit contact ──────────────────────────────────────────────────
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState(emptyContactForm());
  // Tenant's defined CONTACT fields (vertical/custom), rendered as typed inputs.
  const [contactFieldDefs, setContactFieldDefs] = useState<FieldDefinition[]>([]);
  const [definedValues, setDefinedValues] = useState<Record<string, string>>({});
  const definedKeyMap = useMemo(
    () => new Map(contactFieldDefs.map((d) => [d.key.toLowerCase(), d.key])),
    [contactFieldDefs]
  );
  useEffect(() => {
    adminApi
      .getFieldDefinitions('contact', { suppressPermissionLock: true })
      .then(setContactFieldDefs)
      .catch(() => {});
  }, []);
  const [addSaving, setAddSaving] = useState(false);
  const [addError, setAddError] = useState('');
  const [editingContactId, setEditingContactId] = useState<string | null>(null);

  // ── Row actions: status toggle + delete ─────────────────────────────────
  const [actionMenuAnchor, setActionMenuAnchor] = useState<HTMLElement | null>(null);
  const [actionMenuContact, setActionMenuContact] = useState<Contact | null>(null);
  const [statusSavingId, setStatusSavingId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Contact | null>(null);
  const [deleteSaving, setDeleteSaving] = useState(false);
  const [actionError, setActionError] = useState('');

  // ── Bulk selection ──────────────────────────────────────────────────────
  // selectedIds persists across pagination (only cleared on filter change or
  // explicit "clear selection") so a user can build up a cross-page selection.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  // filterSelectionMode = "every contact matching the current search/status/tags
  // filter is selected", independent of which page is on screen or what's in
  // selectedIds. This is what gets sent to the bulk API as filter criteria
  // (never as a materialized id list) so the operation is a single deleteMany/
  // updateMany on the server regardless of how many contacts match.
  const [filterSelectionMode, setFilterSelectionMode] = useState(false);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [bulkDeleteConfirmOpen, setBulkDeleteConfirmOpen] = useState(false);
  const [bulkDeleteConfirmText, setBulkDeleteConfirmText] = useState('');
  const [bulkArchiveConfirmOpen, setBulkArchiveConfirmOpen] = useState(false);
  const [bulkActionError, setBulkActionError] = useState('');
  const [bulkSuccessMessage, setBulkSuccessMessage] = useState('');

  // ── Bulk assign ──────────────────────────────────────────────────────────
  const [bulkAssignOpen, setBulkAssignOpen] = useState(false);
  const [bulkAssignUserId, setBulkAssignUserId] = useState('');
  const [bulkAssignSaving, setBulkAssignSaving] = useState(false);
  // ── Unassign (bulk + single row) ─────────────────────────────────────────
  const [unassignPendingIds, setUnassignPendingIds] = useState<string[]>([]);
  const [unassignApplyRules, setUnassignApplyRules] = useState(true);
  const [unassignSaving, setUnassignSaving] = useState(false);
  // ── Owner-only bulk assign/unassign BY FILTER ("select all N matching") ───
  // Distinct from the two blocks above: those operate on an explicit,
  // materialized id list (selectedIds) and top out at the current page/
  // manually-checked rows. These operate on the filter itself — every
  // contact matching the current view, regardless of page — so the same
  // blast radius as bulk delete/archive but for reassignment. Gated to the
  // tenant owner and behind an explicit consent checkbox (see the dialogs
  // below) since it can silently overwrite assignments the caller never saw.
  const [ownerBulkAssignOpen, setOwnerBulkAssignOpen] = useState(false);
  const [ownerBulkAssignUserId, setOwnerBulkAssignUserId] = useState('');
  const [ownerBulkAssignConsent, setOwnerBulkAssignConsent] = useState(false);
  const [ownerBulkAssignSaving, setOwnerBulkAssignSaving] = useState(false);
  const [ownerBulkUnassignOpen, setOwnerBulkUnassignOpen] = useState(false);
  const [ownerBulkUnassignApplyRules, setOwnerBulkUnassignApplyRules] = useState(true);
  const [ownerBulkUnassignConsent, setOwnerBulkUnassignConsent] = useState(false);
  const [ownerBulkUnassignSaving, setOwnerBulkUnassignSaving] = useState(false);
  // ── Quick assign (single row via action menu) ─────────────────────────────
  const [quickAssignContact, setQuickAssignContact] = useState<Contact | null>(null);
  const [quickAssignUserId, setQuickAssignUserId] = useState('');
  const [quickAssignSaving, setQuickAssignSaving] = useState(false);
  // ── Bulk tag add/remove ───────────────────────────────────────────────────
  const [bulkTagsOpen, setBulkTagsOpen] = useState(false);
  const [bulkTagsOperation, setBulkTagsOperation] = useState<'add' | 'remove'>('add');
  const [bulkTagsValues, setBulkTagsValues] = useState<string[]>([]);
  const [bulkTagsConsent, setBulkTagsConsent] = useState(false);
  // ── Contact activity log (audit trail) ────────────────────────────────────
  const [auditLogOpen, setAuditLogOpen] = useState(false);
  const [auditRows, setAuditRows] = useState<ContactAuditEvent[]>([]);
  const [auditTotal, setAuditTotal] = useState(0);
  const [auditPage, setAuditPage] = useState(1);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditError, setAuditError] = useState('');
  const [auditBulkOnly, setAuditBulkOnly] = useState(false);
  // ── Assignment rules dialog ───────────────────────────────────────────────
  const [assignRulesOpen, setAssignRulesOpen] = useState(false);
  // Rules are tenant-wide routing config — one edit changes who every future
  // contact routes to. Gate on the same tier the backend enforces: saving and
  // applying rules are PUT/POST, so requirePermission('userManagement') derives
  // 'edit'. Module-level access (any tier, read included) would show the button
  // to users whose Save can only ever 403.
  const { hasPermission } = usePermissionAccess();
  const canManageAssignmentRules = hasPermission('userManagement', 'edit');
  /** Backend requires 'full' on activity.chatLogs to (re)assign or unassign —
   *  mirror it here so the buttons aren't clickable affordances that 403. */
  const canAssignContacts = hasPermission('activity.chatLogs', 'full');
  const { user: authUser } = useAuth();
  // Matches the backend's owner-only gate on bulk-assign/unassign BY FILTER
  // (contact.controller.ts's bulkAssignByFilter/bulkUnassignByFilter) exactly
  // — this only controls whether the buttons/dialogs are reachable in this
  // UI; the 403 the backend would return either way is the real enforcement.
  const isOwner = authUser?.role === 'tenant_owner' || authUser?.role === 'superadmin';
  /** Wider than `isOwner`: the audit log is a READ of what the team did, gated
   *  owner/admin server-side (contact.controller.ts's getAuditLog) rather than
   *  owner-only like the destructive by-filter mutations above. */
  const isOwnerOrAdmin = isOwner || authUser?.role === 'tenant_admin';
  /** "New" badge: this row is assigned to ME and I haven't opened it since the
   *  assignment (assigneeSeenAt cleared on every assignment write, stamped by
   *  the mark-read call when the assignee opens the chat). */
  const isNewlyAssignedToMe = (contact: Contact): boolean => {
    if (!authUser?.id || !contact.assignedTo?._id) return false;
    if (String(contact.assignedTo._id) !== String(authUser.id)) return false;
    const assignedAt = contact.routing?.assignedAt;
    if (!assignedAt) return !contact.assigneeSeenAt;
    return !contact.assigneeSeenAt || new Date(contact.assigneeSeenAt) < new Date(assignedAt);
  };

  // ── Import contacts from CSV ────────────────────────────────────────────
  const [importOpen, setImportOpen] = useState(false);
  const [importRows, setImportRows] = useState<Record<string, string>[]>([]);
  const [importMapping, setImportMapping] = useState<Record<string, string>>({});
  const [importTags, setImportTags] = useState<string[]>([]);
  const [importFileName, setImportFileName] = useState('');
  const [importDefaultCountry, setImportDefaultCountry] = useState('IN');
  const [importWorkbook, setImportWorkbook] = useState<XLSX.WorkBook | null>(null);
  const [importSheetNames, setImportSheetNames] = useState<string[]>([]);
  const [importSelectedSheet, setImportSelectedSheet] = useState('');
  const [importSaving, setImportSaving] = useState(false);
  const [importError, setImportError] = useState('');
  const [importResult, setImportResult] = useState<ContactImportResult | null>(null);
  // ── Ownership for this import ────────────────────────────────────────────
  // One mode rather than a set of independent switches, so the precedence can
  // never be ambiguous — see ContactImportAssignment. 'rules' is the default
  // and is exactly what every import did before these controls existed.
  const [importAssignMode, setImportAssignMode] =
    useState<ContactImportAssignment['mode']>('rules');
  const [importAssignUserId, setImportAssignUserId] = useState('');
  const [importAssignFallbackToRules, setImportAssignFallbackToRules] = useState(true);
  const [importAssignOverwriteOwner, setImportAssignOverwriteOwner] = useState(false);

  const fetchContacts = async () => {
    setLoading(true);
    try {
      // Filter → API params goes through the ONE canonical mapper that the
      // saved-segment serializer already uses, instead of a second hand-rolled
      // copy of the same 17-field translation. The two had already drifted:
      // `minAttempts` was nested under `minFailureRate` here but independent
      // there, so a segment could persist a filter the live list silently
      // dropped. One mapper means that class of bug cannot recur.
      const filterParams = toSegmentFilterParamsPayload({
        statusFilter,
        tagFilter,
        segmentFilters: filters,
      });
      // Only the wire FORM differs from the segment payload: arrays go over the
      // query string flattened.
      const { tags, attributes, ...rest } = filterParams;
      const params: ContactListParams = {
        ...rest,
        page,
        limit: CONTACTS_PAGE_SIZE,
        sortBy: 'updatedAt',
        sortOrder: 'desc' as const,
        ...toQueryParams(
          buildContactSelectionSpec({
            search: debouncedSearch,
            statusFilter,
            tagFilter,
            contactedFilter,
            notesFilter,
            filters,
          })
        ),
      };

      const response = await adminApi.getConversations(params);
      setContacts((response.data as Contact[]) || []);
      setTotalCount(response.meta?.total ?? 0);
      // Same filter params, so the split describes exactly these rows. Fired
      // after the list (not in parallel) so a slow/failed summary can never
      // delay or break the table — blank chips are an acceptable degradation.
      adminApi
        .getContactTouchSummary(params as Record<string, unknown>, { suppressPermissionLock: true })
        .then(setTouchSummary)
        .catch(() => setTouchSummary(null));
      setTotalPages(Math.max(1, response.meta?.totalPages ?? 1));
    } catch (error) {
      console.error('Error fetching contacts:', error);
      setContacts([]);
      setTotalCount(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  // ── Bulk selection derived state ────────────────────────────────────────
  const allPageSelected = contacts.length > 0 && contacts.every((c) => selectedIds.has(c._id));
  const somePageSelected = contacts.some((c) => selectedIds.has(c._id));
  const hasSelection = selectedIds.size > 0 || filterSelectionMode;
  const selectionCount = filterSelectionMode ? totalCount : selectedIds.size;
  // The exact spec describing what the list is showing right now — the single
  // object both the fetch and every "select all matching" bulk action use, so
  // the two can never describe different sets.
  const currentSelectionSpec = buildContactSelectionSpec({
    search: debouncedSearch,
    statusFilter,
    tagFilter,
    contactedFilter,
    notesFilter,
    filters,
  });
  // Every filter dimension is now expressible in a bulk call (the backend
  // resolves the same spec through the same builder), so "select all matching"
  // no longer has to be hidden when a drawer filter is active — it only needs
  // SOME filter to be active, since an unrestricted spec would mean the whole
  // account and is refused on both sides.
  const hasActiveFilter = specHasAnyFilter(currentSelectionSpec);
  const canSelectAllFilter = hasActiveFilter && totalCount > 0 && !filterSelectionMode;

  /** Audit log is lazy — nothing is fetched until the dialog is actually opened,
   *  so the Contacts page's own load is untouched by this feature. */
  const fetchAuditLog = async (page: number, bulkOnly: boolean) => {
    setAuditLoading(true);
    setAuditError('');
    try {
      const res = await adminApi.getContactAuditLog({
        page,
        limit: AUDIT_PAGE_SIZE,
        ...(bulkOnly ? { bulkOnly: true } : {}),
      });
      setAuditRows(res.events);
      setAuditTotal(res.total);
      setAuditPage(page);
    } catch (e: any) {
      setAuditError(
        e?.response?.data?.error?.message || e.message || 'Could not load the activity log'
      );
      setAuditRows([]);
      setAuditTotal(0);
    } finally {
      setAuditLoading(false);
    }
  };

  const openAuditLog = () => {
    setAuditLogOpen(true);
    void fetchAuditLog(1, auditBulkOnly);
  };

  const describeActiveFilters = (): string => {
    const parts: string[] = [];
    if (tagFilter.length) parts.push(`tagged ${tagFilter.map((t) => `"${t}"`).join(', ')}`);
    if (statusFilter !== 'all') parts.push(`with status "${statusFilter}"`);
    if (debouncedSearch) parts.push(`matching "${debouncedSearch}"`);
    return parts.length ? parts.join(' and ') : 'in this list';
  };

  const toggleRow = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedIds((prev) => {
      // Deselecting one row while "all matching filters" is active: fall back to
      // "every row on this page except this one" — the only concrete set we can
      // represent without re-fetching every matching id.
      const base = filterSelectionMode ? new Set(contacts.map((c) => c._id)) : prev;
      const next = new Set(base);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    if (filterSelectionMode) setFilterSelectionMode(false);
  };

  const toggleAllPage = () => {
    if (filterSelectionMode) {
      // Header checkbox reads as checked because of filter-selection mode;
      // clicking it again means "deselect everything", not "select this page".
      setFilterSelectionMode(false);
      setSelectedIds(new Set());
      return;
    }
    if (allPageSelected) {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        contacts.forEach((c) => next.delete(c._id));
        return next;
      });
    } else {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        contacts.forEach((c) => next.add(c._id));
        return next;
      });
    }
  };

  const clearSelection = () => {
    setSelectedIds(new Set());
    setFilterSelectionMode(false);
  };

  /**
   * Resets EVERY filter dimension, not just the drawer's `SegmentFilters`.
   * Previously both "Clear all" buttons only called
   * `setFilters(emptySegmentFilters())` — which resets source/date/engagement/
   * frequency/assignedTo/quality-risk/tags-combinator/attributes, but leaves
   * `search`, `statusFilter`, `tagFilter`, `contactedFilter` and `notesFilter`
   * untouched, since those four live in their own top-bar state, not inside
   * `SegmentFilters`. A user with Status=Archived + 2 tags + a search term +
   * a drawer filter would click "Clear all" and still have three of those four
   * silently still restricting the list. One shared reset so both the
   * chip-row button and the drawer's own footer button can never drift apart
   * on what "all" means again.
   */
  const clearAllContactFilters = () => {
    setSearch('');
    // Search is cleared in the SAME patch as everything else. Leaving it to the
    // 300ms debounce meant a second request, and 300ms where the box looked
    // empty while the list was still filtered by the old term.
    patchQuery({
      search: '',
      status: 'all',
      tags: [],
      contacted: 'all',
      notes: 'all',
      ...spreadSegmentFilters(emptySegmentFilters()),
    });
    setFilterDraft(emptySegmentFilters());
  };

  // Filter-mode sends the current search/status/tags as filter criteria — never
  // a materialized id list — so the backend resolves it as a single deleteMany/
  // updateMany regardless of match count (no N+1, no client-side id fetch).
  // Filter mode sends the FULL selection spec — never a materialized id list —
  // so the backend resolves it through the same builder the list ran on, as a
  // single deleteMany/updateMany regardless of match count (no N+1, no
  // client-side id fetch). Arrays travel as arrays: the JSON bulk endpoints
  // parse them natively, so a tag containing a comma stays one tag.
  // `confirmed: true` rides on every filter-mode call: the backend requires it
  // uniformly for delete / archive / tags / assign / unassign, because a filter
  // resolves server-side to rows the caller never enumerated. Id mode is a set
  // the user ticked by hand and needs no second confirmation.
  const buildBulkPayload = (): ContactBulkSelection =>
    filterSelectionMode
      ? { ...currentSelectionSpec, confirmed: true }
      : { ids: Array.from(selectedIds) };

  const handleBulkDelete = async () => {
    setBulkSaving(true);
    setBulkActionError('');
    try {
      // The typed-DELETE box is the UI half of the backend's `confirmed` gate,
      // not a substitute for it — buildBulkPayload sends the flag.
      const result = await adminApi.bulkDeleteContacts(buildBulkPayload());
      clearSelection();
      setBulkDeleteConfirmOpen(false);
      setBulkDeleteConfirmText('');
      setBulkSuccessMessage(
        `Deleted ${result.deletedCount} contact${result.deletedCount === 1 ? '' : 's'}`
      );
      fetchContacts();
    } catch (e: any) {
      setBulkActionError(e?.response?.data?.error?.message || e.message || 'Bulk delete failed');
    } finally {
      setBulkSaving(false);
    }
  };

  const openBulkTags = (operation: 'add' | 'remove') => {
    setBulkTagsOperation(operation);
    setBulkTagsValues([]);
    setBulkTagsConsent(false);
    setBulkActionError('');
    setBulkTagsOpen(true);
  };

  const handleBulkTags = async () => {
    if (bulkTagsValues.length === 0) return;
    setBulkSaving(true);
    setBulkActionError('');
    try {
      // The consent checkbox is the UI half of the backend's `confirmed` gate,
      // not a replacement for it — buildBulkPayload sends the flag.
      const result = await adminApi.bulkUpdateContactTags(
        buildBulkPayload(),
        bulkTagsOperation,
        bulkTagsValues
      );
      clearSelection();
      setBulkTagsOpen(false);
      const verb = bulkTagsOperation === 'add' ? 'Tagged' : 'Untagged';
      setBulkSuccessMessage(
        result.modifiedCount === result.matchedCount
          ? `${verb} ${result.modifiedCount} contact${result.modifiedCount === 1 ? '' : 's'}`
          : `${verb} ${result.modifiedCount} of ${result.matchedCount} matched — the rest already had exactly these tags`
      );
      fetchContacts();
      // The tag vocabulary powering every tag picker on this page is a distinct
      // endpoint, so a newly-created tag would be missing from it until reload.
      // Must INVALIDATE rather than refetch — with a cache in place, a plain
      // refetch would be handed the pre-mutation list straight back.
      refreshAllTags();
    } catch (e: any) {
      setBulkActionError(
        e?.response?.data?.error?.message || e.message || 'Bulk tag update failed'
      );
    } finally {
      setBulkSaving(false);
    }
  };

  const handleBulkArchive = async () => {
    setBulkSaving(true);
    setBulkActionError('');
    try {
      const result = await adminApi.bulkSetContactsStatus(buildBulkPayload(), 'archived');
      clearSelection();
      setBulkArchiveConfirmOpen(false);
      setBulkSuccessMessage(
        `Archived ${result.modifiedCount} contact${result.modifiedCount === 1 ? '' : 's'}`
      );
      fetchContacts();
    } catch (e: any) {
      setBulkActionError(e?.response?.data?.error?.message || e.message || 'Bulk archive failed');
    } finally {
      setBulkSaving(false);
    }
  };

  // Deep links into this page (Campaign Run History's "Never-delivered contacts"
  // button arrives as `?advanced=<value>&campaignId=<id>`) need no bootstrap
  // effect any more: those params ARE spec keys, so the first render already
  // holds them. The old effect seeded them AFTER the first fetch had gone out
  // with empty filters, which is why a deep link used to cost two requests.

  // Debounce free-text search before it reaches the query (and so the API).
  // A no-op patch — this firing on mount with the value it just read out of the
  // URL — is dropped inside the hook, so it cannot trigger a second fetch.
  useEffect(() => {
    const timer = setTimeout(() => patchQuery({ search: search.trim() }), 300);
    return () => clearTimeout(timer);
  }, [search, patchQuery]);

  // Returning to page 1 on a filter change is handled by the hook, which knows
  // which spec field is the page.

  useEffect(() => {
    fetchContacts();
  }, [debouncedSearch, statusFilter, contactedFilter, notesFilter, tagFilter, filters, page]);

  // Clear selection whenever the filter criteria change (not on page change —
  // selection is meant to persist across pagination so a cross-page selection
  // can be built up).
  useEffect(() => {
    setSelectedIds(new Set());
    setFilterSelectionMode(false);
  }, [debouncedSearch, statusFilter, contactedFilter, notesFilter, tagFilter, filters]);

  // The reference lists below barely change, but this page refetched all of them
  // on every mount — and it remounts every time you come back from a contact.
  // Cached, so a return trip costs only the one request that depends on the
  // query. Each normalises inside its loader, so the cache stores the shape the
  // UI renders rather than a raw response.
  const [allTags, refreshAllTags] = useCachedResource(
    'contacts:tags',
    () =>
      adminApi
        .getContactTags({ suppressPermissionLock: true })
        .then((tags) => (Array.isArray(tags) ? tags : [])),
    EMPTY_STRINGS
  );

  const [attributeKeys, refreshAttributeKeys] = useCachedResource(
    'contacts:attributeKeys',
    () =>
      adminApi
        .getContactAttributeKeys({ suppressPermissionLock: true })
        .then((keys) => (Array.isArray(keys) ? keys : [])),
    EMPTY_STRINGS
  );

  // Populates the "Assigned to" picker, which is in the always-visible filter bar.
  const [teamMembers] = useCachedResource(
    'contacts:teamDirectory',
    () =>
      adminApi
        .getTeamDirectory({ suppressPermissionLock: true })
        .then((list) => (Array.isArray(list) ? list : [])),
    NO_TEAM_MEMBERS
  );

  const [savedSegments, refreshSegments] = useCachedResource(
    'contacts:segments',
    () =>
      adminApi
        .getSegments({ suppressPermissionLock: true })
        .then((list) => (Array.isArray(list) ? list : [])),
    NO_SEGMENTS
  );

  // Only the 'neverDeliveredThisCampaign' picker inside the Filters drawer reads
  // this, and it is the one unbounded payload of the five (every campaign in the
  // tenant). Deferred until the drawer is actually opened; most sessions never
  // pay for it, and once loaded it is cached like the rest.
  const [campaigns] = useCachedResource(
    'contacts:campaignOptions',
    () =>
      adminApi.getCampaigns(undefined, { suppressPermissionLock: true }).then((list) =>
        (Array.isArray(list) ? list : [])
          .filter((c) => c && typeof c._id === 'string' && typeof c.name === 'string')
          .map((c) => ({ _id: c._id, name: c.name }))
      ),
    NO_CAMPAIGNS,
    {
      enabled:
        filtersDrawerOpen || filterDraft.advancedFilter === 'neverDeliveredThisCampaign',
    }
  );

  useEffect(() => {
    try {
      const raw = localStorage.getItem('contacts_visible_fields');
      if (raw) {
        const parsed = JSON.parse(raw);
        setVisibleFields((prev) => ({ ...prev, ...parsed }));
      }
    } catch (error) {
      console.error('Failed to load contact attributes config:', error);
    }
  }, []);

  const handleSaveAttributes = () => {
    localStorage.setItem('contacts_visible_fields', JSON.stringify(visibleFields));
    setAttributesOpen(false);
  };

  // ── Saved dynamic segments (Tier 3) ─────────────────────────────────────

  /** Re-populates every filter control from a saved segment's stored spec — the
   *  exact inverse of toSegmentFilterParamsPayload. The existing fetch effects
   *  then re-run through the SAME validated resolution path a live filter uses. */
  const applySegment = (segment: Segment) => {
    const p = segment.filterParams;
    // One patch: loading a segment is a single query change, so it is a single
    // render and a single request rather than one per control it touches.
    patchQuery({
      status: p.status || 'all',
      tags: p.tags || [],
      ...spreadSegmentFilters(segmentParamsToFilters(p)),
    });
    setSegmentsMenuAnchor(null);
  };

  const openSaveSegmentDialog = () => {
    setSaveSegmentName('');
    setSaveSegmentDescription('');
    setSaveSegmentError('');
    setSegmentsMenuAnchor(null);
    setSaveSegmentOpen(true);
  };

  const handleSaveSegment = async () => {
    const name = saveSegmentName.trim();
    if (!name) {
      setSaveSegmentError('Name is required');
      return;
    }
    setSaveSegmentSaving(true);
    setSaveSegmentError('');
    try {
      const filterParams = toSegmentFilterParamsPayload({
        statusFilter,
        tagFilter,
        segmentFilters: filters,
      });
      const trimmedDescription = saveSegmentDescription.trim();
      await adminApi.createSegment({
        name,
        ...(trimmedDescription ? { description: trimmedDescription } : {}),
        filterParams,
      });
      setSaveSegmentOpen(false);
      refreshSegments();
    } catch (e: any) {
      setSaveSegmentError(
        e?.response?.data?.error?.message || e.message || 'Failed to save segment'
      );
    } finally {
      setSaveSegmentSaving(false);
    }
  };

  const handleDeleteSegment = async () => {
    if (!segmentDeleteTarget) return;
    setSegmentActionError('');
    try {
      await adminApi.deleteSegment(segmentDeleteTarget._id);
      setSegmentDeleteTarget(null);
      refreshSegments();
    } catch (e: any) {
      setSegmentActionError(
        e?.response?.data?.error?.message || e.message || 'Failed to delete segment'
      );
    }
  };

  const openEditSegmentDialog = (seg: Segment) => {
    setEditSegmentTarget(seg);
    setEditSegmentName(seg.name);
    setEditSegmentDescription(seg.description || '');
    setEditSegmentUpdateFilters(false);
    setEditSegmentError('');
    setSegmentsMenuAnchor(null);
  };

  const handleUpdateSegment = async () => {
    if (!editSegmentTarget) return;
    const name = editSegmentName.trim();
    if (!name) {
      setEditSegmentError('Name is required');
      return;
    }
    setEditSegmentSaving(true);
    setEditSegmentError('');
    try {
      const trimmedDescription = editSegmentDescription.trim();
      await adminApi.updateSegment(editSegmentTarget._id, {
        name,
        description: trimmedDescription,
        // Opt-in only — renaming/re-describing a segment must never silently
        // change what it filters. Only sent when the admin explicitly checked
        // the box, and always reflects the CURRENTLY APPLIED (committed) filter
        // state — the same source of truth "Save as segment" itself uses.
        ...(editSegmentUpdateFilters
          ? {
              filterParams: toSegmentFilterParamsPayload({
                statusFilter,
                tagFilter,
                segmentFilters: filters,
              }),
            }
          : {}),
      });
      setEditSegmentTarget(null);
      refreshSegments();
    } catch (e: any) {
      setEditSegmentError(
        e?.response?.data?.error?.message || e.message || 'Failed to update segment'
      );
    } finally {
      setEditSegmentSaving(false);
    }
  };

  // ── Add / Edit contact ──────────────────────────────────────────────────

  const openAddContact = () => {
    setEditingContactId(null);
    setAddForm(emptyContactForm());
    setDefinedValues({});
    setAddError('');
    setAddOpen(true);
  };

  const openEditContact = (contact: Contact) => {
    setEditingContactId(contact._id);
    // Split existing attributes: those matching a defined field feed the typed
    // DynamicFields inputs; the rest stay in the free-form Custom Fields list —
    // so a defined field never shows twice.
    const dv: Record<string, string> = {};
    const freeForm: Array<{ key: string; value: string }> = [];
    for (const attr of contact.customAttributes || []) {
      const defKey = definedKeyMap.get(attr.key.toLowerCase());
      if (defKey) dv[defKey] = String(attr.value);
      else freeForm.push({ key: attr.key, value: String(attr.value) });
    }
    setDefinedValues(dv);
    setAddForm({
      // The contacts list falls back to the literal "Unknown" when no name
      // is set — don't let that placeholder round-trip into the form as a
      // real name.
      name: contact.customerName && contact.customerName !== 'Unknown' ? contact.customerName : '',
      phone: contact.customerPhone || '',
      email: contact.customerEmail || '',
      countryCode: contact.customerCountryCode || contact.customerCountry || '',
      tags: contact.tags || [],
      customAttributes: freeForm,
    });
    setAddError('');
    setAddOpen(true);
  };

  // ── Row actions: status toggle + delete ─────────────────────────────────

  const openActionMenu = (e: React.MouseEvent<HTMLElement>, contact: Contact) => {
    e.stopPropagation();
    setActionMenuAnchor(e.currentTarget);
    setActionMenuContact(contact);
  };

  const closeActionMenu = () => {
    setActionMenuAnchor(null);
    setActionMenuContact(null);
  };

  const handleToggleStatus = async () => {
    const contact = actionMenuContact;
    if (!contact) return;
    const nextStatus = contact.status === 'archived' ? 'active' : 'archived';
    closeActionMenu();
    setStatusSavingId(contact._id);
    setActionError('');
    try {
      await adminApi.setContactStatus(contact._id, nextStatus);
      fetchContacts();
    } catch (e: any) {
      setActionError(
        e?.response?.data?.error?.message || e.message || 'Failed to update contact status'
      );
    } finally {
      setStatusSavingId(null);
    }
  };

  const openDeleteConfirm = () => {
    setDeleteTarget(actionMenuContact);
    closeActionMenu();
  };

  const handleDeleteContact = async () => {
    if (!deleteTarget) return;
    setDeleteSaving(true);
    setActionError('');
    try {
      await adminApi.deleteContact(deleteTarget._id);
      setDeleteTarget(null);
      fetchContacts();
    } catch (e: any) {
      setActionError(e?.response?.data?.error?.message || e.message || 'Failed to delete contact');
    } finally {
      setDeleteSaving(false);
    }
  };

  const addCustomAttributeField = () => {
    setAddForm((prev) => ({
      ...prev,
      customAttributes: [...prev.customAttributes, { key: '', value: '' }],
    }));
  };

  const updateCustomAttributeField = (
    index: number,
    patch: Partial<{ key: string; value: string }>
  ) => {
    setAddForm((prev) => {
      const current = prev.customAttributes[index];
      if (!current) return prev;
      const next = [...prev.customAttributes];
      next[index] = { key: patch.key ?? current.key, value: patch.value ?? current.value };
      return { ...prev, customAttributes: next };
    });
  };

  const removeCustomAttributeField = (index: number) => {
    setAddForm((prev) => ({
      ...prev,
      customAttributes: prev.customAttributes.filter((_, i) => i !== index),
    }));
  };

  const handleSaveContact = async () => {
    if (!isValidPhone(addForm.phone)) {
      setAddError('Enter a valid phone number, including the country code.');
      return;
    }

    const customAttributes = addForm.customAttributes
      .map((field) => ({ key: field.key.trim(), value: field.value.trim() }))
      .filter((field) => field.key);
    const seenKeys = new Set<string>();
    for (const field of customAttributes) {
      const lowerKey = field.key.toLowerCase();
      if (seenKeys.has(lowerKey)) {
        setAddError(`Custom field "${field.key}" is entered more than once.`);
        return;
      }
      seenKeys.add(lowerKey);
    }

    // Merge the typed defined-field values into customAttributes (defined keys win
    // over any free-form field with the same key). No definitions => unchanged.
    const definedEntries = Object.entries(definedValues)
      .map(([key, value]) => ({ key: key.trim(), value: String(value).trim() }))
      .filter((e) => e.key && e.value);
    const definedKeysLower = new Set(definedEntries.map((e) => e.key.toLowerCase()));
    const mergedAttributes = [
      ...definedEntries,
      ...customAttributes.filter((f) => !definedKeysLower.has(f.key.toLowerCase())),
    ];

    setAddSaving(true);
    setAddError('');
    try {
      const name = addForm.name.trim();
      const email = addForm.email.trim();
      const countryCode = addForm.countryCode.trim();
      const tags = cleanTags(addForm.tags);

      if (editingContactId) {
        await adminApi.updateContact(editingContactId, {
          name,
          phone: addForm.phone.trim(),
          email,
          countryCode,
          tags,
          customAttributes: mergedAttributes,
        });
      } else {
        await adminApi.createContact({
          ...(name ? { name } : {}),
          phone: addForm.phone.trim(),
          ...(email ? { email } : {}),
          ...(countryCode ? { countryCode } : {}),
          tags,
          customAttributes: mergedAttributes,
        });
      }
      setAddOpen(false);
      setDefinedValues({});
      fetchContacts();
      // Invalidate, so a tag or attribute key this write just introduced is not
      // hidden behind the cached pre-mutation vocabulary.
      refreshAllTags();
      refreshAttributeKeys();
    } catch (e: any) {
      setAddError(e?.response?.data?.error?.message || e.message || 'Failed to save contact');
    } finally {
      setAddSaving(false);
    }
  };

  // ── Import contacts from CSV ────────────────────────────────────────────

  // Every CSV header, including ones the auto-detector didn't recognize —
  // every row object carries all headers as keys regardless of mapping.
  const importHeaders = useMemo(() => Object.keys(importRows[0] || {}), [importRows]);

  const getImportFieldSelectValue = (header: string): string => {
    const mapped = importMapping[header];
    if (!mapped) return '';
    return mapped.startsWith(CUSTOM_FIELD_MAPPING_PREFIX) ? CUSTOM_FIELD_SELECT_VALUE : mapped;
  };

  const setImportFieldMapping = (header: string, value: string) => {
    setImportMapping((prev) => {
      const next = { ...prev };
      if (!value) {
        delete next[header];
      } else if (value === CUSTOM_FIELD_SELECT_VALUE) {
        next[header] = CUSTOM_FIELD_MAPPING_PREFIX;
      } else {
        next[header] = value;
      }
      return next;
    });
  };

  const setImportCustomFieldKey = (header: string, key: string) => {
    setImportMapping((prev) => ({ ...prev, [header]: `${CUSTOM_FIELD_MAPPING_PREFIX}${key}` }));
  };

  const openImportContacts = () => {
    setImportRows([]);
    setImportMapping({});
    setImportTags([]);
    setImportFileName('');
    setImportDefaultCountry('IN');
    setImportWorkbook(null);
    setImportSheetNames([]);
    setImportSelectedSheet('');
    setImportError('');
    setImportResult(null);
    // Ownership resets to the historical default on every open, so a previous
    // import's choice can never silently carry into the next file.
    setImportAssignMode('rules');
    setImportAssignUserId('');
    setImportAssignFallbackToRules(true);
    setImportAssignOverwriteOwner(false);
    setImportOpen(true);
  };

  /** Loads a sheet's rows into the mapping UI; used both on file pick and on sheet switch. */
  const loadContactsSheet = (workbook: XLSX.WorkBook, sheetName: string) => {
    const { rows, fieldMapping } = parseContactsExcelSheet(workbook, sheetName);
    if (rows.length === 0) {
      setImportError(
        'No rows found in this sheet. Make sure it has a header row plus at least one data row.'
      );
      setImportRows([]);
      setImportMapping({});
      return;
    }
    if (!Object.values(fieldMapping).includes('phone')) {
      setImportError(
        'Could not find a "phone" column. Expected a header like "phone" or "mobile".'
      );
    } else {
      setImportError('');
    }
    setImportRows(rows);
    setImportMapping(fieldMapping);
  };

  const handleImportFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportError('');
    setImportResult(null);
    setImportFileName(file.name);
    setImportWorkbook(null);
    setImportSheetNames([]);
    setImportSelectedSheet('');
    try {
      const isExcel = /\.(xlsx|xls)$/i.test(file.name);
      if (!isExcel) {
        const { rows, fieldMapping } = await parseContactsCsvFile(file);
        if (rows.length === 0) {
          setImportError(
            'No rows found in this file. Make sure it has a header row plus at least one data row.'
          );
          setImportRows([]);
          setImportMapping({});
          return;
        }
        if (!Object.values(fieldMapping).includes('phone')) {
          setImportError(
            'Could not find a "phone" column. Expected a header like "phone" or "mobile".'
          );
        }
        setImportRows(rows);
        setImportMapping(fieldMapping);
        return;
      }

      const workbook = await readContactsWorkbook(file);
      const sheetNames = workbook.SheetNames;
      const firstSheetName = sheetNames[0];
      if (!firstSheetName) {
        setImportError('This workbook has no sheets.');
        setImportRows([]);
        setImportMapping({});
        return;
      }
      setImportWorkbook(workbook);
      setImportSheetNames(sheetNames);
      setImportSelectedSheet(firstSheetName);
      loadContactsSheet(workbook, firstSheetName);
    } catch (err: any) {
      setImportError(err.message || 'Failed to read file');
    }
  };

  const handleImportSheetChange = (sheetName: string) => {
    setImportSelectedSheet(sheetName);
    setImportResult(null);
    if (importWorkbook) loadContactsSheet(importWorkbook, sheetName);
  };

  /** Whether a column is currently mapped to the owner-email target. */
  const importHasOwnerColumn = useMemo(
    () => Object.values(importMapping).includes(ASSIGNEE_FIELD_MAPPING_TARGET),
    [importMapping]
  );

  /** Whether a column is currently mapped to the note target. */
  const importHasNoteColumn = useMemo(
    () => Object.values(importMapping).includes(NOTE_FIELD_MAPPING_TARGET),
    [importMapping]
  );

  /**
   * Assigning through an import is gated on the same caller tier as every other
   * assign action here (`canAssignContacts`), and replacing an owner a contact
   * already has needs owner/admin on top — the stricter tier bulkAssignByFilter
   * uses. Mirrored here only so the controls are hidden rather than offered and
   * then rejected; contact.controller.ts enforces both regardless.
   */
  const canOverwriteOwnerOnImport =
    canAssignContacts && (isOwner || authUser?.role === 'tenant_admin');

  /**
   * Owner addresses in the file that match no active workspace member, found
   * WITHOUT a round trip — the team directory is already loaded for the
   * "Assigned to" filter. This is an early warning, not the verdict: the
   * directory carries no permission data, so a member who lacks Full access to
   * Chat Logs still looks fine here and is only caught by the preview.
   */
  const importUnknownOwnerEmails = useMemo(() => {
    if (!importHasOwnerColumn || importRows.length === 0) return [];
    const ownerHeaders = Object.entries(importMapping)
      .filter(([, target]) => target === ASSIGNEE_FIELD_MAPPING_TARGET)
      .map(([header]) => header);
    if (!ownerHeaders.length) return [];
    const known = new Set(
      teamMembers.filter((m) => m.isActive !== false).map((m) => (m.email || '').toLowerCase())
    );
    const unknown = new Set<string>();
    for (const row of importRows) {
      for (const header of ownerHeaders) {
        const email = String(row[header] ?? '').trim().toLowerCase();
        if (email && !known.has(email)) unknown.add(email);
      }
    }
    return Array.from(unknown);
  }, [importHasOwnerColumn, importRows, importMapping, teamMembers]);

  /**
   * The assignment block sent with both the preview and the commit, so the
   * numbers the preview showed are the numbers the commit produces. Returns
   * undefined for the default, which keeps the request byte-identical to what
   * it was before these controls existed.
   */
  const buildImportAssignment = (): ContactImportAssignment | undefined => {
    if (importAssignMode === 'rules') return undefined;
    if (importAssignMode === 'none') return { mode: 'none' };
    if (importAssignMode === 'single') {
      return {
        mode: 'single',
        userId: importAssignUserId,
        overwriteExistingOwner: importAssignOverwriteOwner,
      };
    }
    return {
      mode: 'column',
      fallbackToRules: importAssignFallbackToRules,
      overwriteExistingOwner: importAssignOverwriteOwner,
    };
  };

  /**
   * An ownership choice the backend would reject: 'single' with nobody picked,
   * or 'column' with no column mapped to it. Blocking the button here turns a
   * guaranteed 400 into an affordance that is simply not clickable yet.
   */
  const importAssignmentIncomplete =
    (importAssignMode === 'single' && !importAssignUserId) ||
    (importAssignMode === 'column' && !importHasOwnerColumn);

  /** Human-readable precedence for the dialog, recomputed from the current
   *  choices. Showing the order back is the whole point: an admin should not
   *  have to infer who wins from three separate controls. */
  const importOwnerPrecedence = useMemo(() => {
    const rungs: string[] = [];
    // Dropped entirely rather than annotated when the replace box is ticked:
    // the rung genuinely no longer applies, and saying so is shorter than
    // qualifying it.
    if (!importAssignOverwriteOwner) rungs.push('existing owner');
    if (importAssignMode === 'column') rungs.push('owner column');
    if (importAssignMode === 'single') rungs.push('your pick');
    if (
      importAssignMode === 'rules' ||
      (importAssignMode === 'column' && importAssignFallbackToRules)
    ) {
      rungs.push('rules');
    }
    rungs.push('unassigned');
    return rungs.join(' → ');
  }, [importAssignMode, importAssignOverwriteOwner, importAssignFallbackToRules]);

  const downloadContactsTemplate = () => {
    const blob = new Blob([CONTACT_CSV_TEMPLATE], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = 'contacts-import-template.csv';
    anchor.click();
    URL.revokeObjectURL(url);
  };

  /** Step 1: dry-run against the backend's real matching logic so the user sees
   *  new-vs-update counts (and can catch an accidental re-import) before anything writes. */
  const handlePreviewImportContacts = async () => {
    if (importRows.length === 0) return;
    setImportSaving(true);
    setImportError('');
    try {
      // Conditional spread, not `assignment: maybeUndefined` —
      // exactOptionalPropertyTypes distinguishes "absent" from "undefined", and
      // absent is what keeps the default request byte-identical to what it was
      // before these controls existed.
      const assignment = buildImportAssignment();
      const result = await adminApi.importContacts(
        importRows,
        importMapping,
        cleanTags(importTags),
        {
          dryRun: true,
          defaultCountry: importDefaultCountry,
          ...(assignment ? { assignment } : {}),
        }
      );
      setImportResult(result);
    } catch (e: any) {
      if (!showPlanLockIfTagged(e)) {
        setImportError(
          e?.response?.data?.error?.message || e.message || 'Failed to preview import'
        );
      }
    } finally {
      setImportSaving(false);
    }
  };

  /** Step 2: the user reviewed the preview counts and confirmed — commit for real. */
  const handleConfirmImportContacts = async () => {
    if (importRows.length === 0) return;
    setImportSaving(true);
    setImportError('');
    try {
      // Same block the preview was computed with, so the commit cannot resolve
      // ownership differently from the numbers the user just approved.
      const assignment = buildImportAssignment();
      const result = await adminApi.importContacts(
        importRows,
        importMapping,
        cleanTags(importTags),
        {
          defaultCountry: importDefaultCountry,
          ...(assignment ? { assignment } : {}),
        }
      );
      setImportResult(result);
      fetchContacts();
      refreshUsage(); // refresh the Contacts usage chip after a bulk import
      // Invalidate, so a tag or attribute key this write just introduced is not
      // hidden behind the cached pre-mutation vocabulary.
      refreshAllTags();
      refreshAttributeKeys();
    } catch (e: any) {
      if (!showPlanLockIfTagged(e)) {
        setImportError(
          e?.response?.data?.error?.message || e.message || 'Failed to import contacts'
        );
      }
    } finally {
      setImportSaving(false);
    }
  };

  const handleBulkAssign = async () => {
    if (!bulkAssignUserId) return;
    setBulkAssignSaving(true);
    setBulkActionError('');
    try {
      const result = await adminApi.bulkAssignContacts(Array.from(selectedIds), bulkAssignUserId);
      clearSelection();
      setBulkAssignOpen(false);
      setBulkAssignUserId('');
      const msg = `Assigned ${result.assigned} contact${result.assigned === 1 ? '' : 's'}`;
      setBulkSuccessMessage(
        result.failed?.length ? `${msg} (${result.failed.length} could not be assigned)` : msg
      );
      fetchContacts();
    } catch (e: any) {
      setBulkActionError(e?.response?.data?.error?.message || e.message || 'Bulk assign failed');
    } finally {
      setBulkAssignSaving(false);
    }
  };

  const handleUnassign = async () => {
    if (unassignPendingIds.length === 0) return;
    setUnassignSaving(true);
    setBulkActionError('');
    try {
      const result = await adminApi.bulkUnassignContacts(unassignPendingIds, unassignApplyRules);
      setUnassignPendingIds([]);
      clearSelection();
      const n = result.unassigned;
      const r = result.rulesApplied;
      let msg = `${n} unassigned`;
      if (r > 0) msg += `, ${r} re-assigned by rules`;
      if (result.failed?.length) msg += ` (${result.failed.length} failed)`;
      setBulkSuccessMessage(msg);
      fetchContacts();
    } catch (e: any) {
      setBulkActionError(e?.response?.data?.error?.message || e.message || 'Unassign failed');
    } finally {
      setUnassignSaving(false);
    }
  };

  const handleOwnerBulkAssign = async () => {
    if (!ownerBulkAssignUserId || !ownerBulkAssignConsent) return;
    setOwnerBulkAssignSaving(true);
    setBulkActionError('');
    try {
      const result = await adminApi.bulkAssignContactsByFilter(
        buildBulkPayload() as {
          tags?: string[];
          status?: string;
          search?: string;
          matchMode?: 'all' | 'any';
        },
        ownerBulkAssignUserId
      );
      clearSelection();
      setOwnerBulkAssignOpen(false);
      setOwnerBulkAssignUserId('');
      setOwnerBulkAssignConsent(false);
      setBulkSuccessMessage(
        `Reassigned ${result.assigned} contact${result.assigned === 1 ? '' : 's'}`
      );
      fetchContacts();
    } catch (e: any) {
      setBulkActionError(e?.response?.data?.error?.message || e.message || 'Bulk reassign failed');
    } finally {
      setOwnerBulkAssignSaving(false);
    }
  };

  const handleOwnerBulkUnassign = async () => {
    if (!ownerBulkUnassignConsent) return;
    setOwnerBulkUnassignSaving(true);
    setBulkActionError('');
    try {
      const result = await adminApi.bulkUnassignContactsByFilter(
        buildBulkPayload() as {
          tags?: string[];
          status?: string;
          search?: string;
          matchMode?: 'all' | 'any';
        },
        ownerBulkUnassignApplyRules
      );
      clearSelection();
      setOwnerBulkUnassignOpen(false);
      setOwnerBulkUnassignConsent(false);
      let msg = `${result.unassigned} unassigned`;
      if (result.rulesApplied > 0) msg += `, ${result.rulesApplied} re-assigned by rules`;
      setBulkSuccessMessage(msg);
      fetchContacts();
    } catch (e: any) {
      setBulkActionError(e?.response?.data?.error?.message || e.message || 'Bulk unassign failed');
    } finally {
      setOwnerBulkUnassignSaving(false);
    }
  };

  const handleQuickAssign = async () => {
    if (!quickAssignContact || !quickAssignUserId) return;
    setQuickAssignSaving(true);
    setActionError('');
    try {
      await adminApi.assignConversation(quickAssignContact._id, quickAssignUserId);
      setQuickAssignContact(null);
      setQuickAssignUserId('');
      fetchContacts();
    } catch (e: any) {
      setActionError(e?.response?.data?.error?.message || e.message || 'Failed to assign contact');
    } finally {
      setQuickAssignSaving(false);
    }
  };

  /** Per-contact CRM record. Same /admin-vs-/ prefix rule as openChatLogs below. */
  const openContactDetail = (contact: Contact) => {
    const prefix = location.pathname.startsWith('/admin') ? '/admin' : '';
    // `fromList` tells the detail page its Back can safely be a real history
    // POP, which restores this exact URL — filters, page and all. Navigating to
    // the bare path instead would land on an unfiltered page 1.
    // siblingIds carries the CURRENT page's ordered ids so the detail view can
    // step Prev/Next through the same filtered, sorted set the user is looking
    // at. Both are router state (not a query param): view context, not
    // addressable state — a shared/refreshed URL simply loses it and hides
    // the controls.
    navigate(`${prefix}/contacts/${contact._id}`, {
      state: { fromList: true, siblingIds: contacts.map((c) => c._id) },
    });
  };

  const openChatLogs = (contact: Contact) => {
    const isAdminPath = location.pathname.startsWith('/admin');
    const basePath = isAdminPath ? '/admin/activity/chat-logs' : '/activity/chat-logs';
    const params = new URLSearchParams();
    params.set('conversationId', contact._id);
    params.set('phone', contact.customerPhone);
    navigate(`${basePath}?${params.toString()}`);
  };

  const contactCountLabel = useMemo(() => {
    if (loading) return 'Loading contacts...';
    return `${totalCount} contact${totalCount === 1 ? '' : 's'}`;
  }, [totalCount, loading]);

  // One removable chip per ACTUALLY-active filter (mirrors fetchContacts' own "is this
  // really being sent" logic — a filter picked but missing its required sub-value never
  // gets a chip). This is what makes "what's currently filtering this list" answerable
  // at a glance instead of requiring the drawer to be reopened and re-read field by field.
  // Tag combinator — 'any' → $in (OR/union), 'all' → $all (AND/intersection). It
  // lives on SegmentFilters next to the drawer's custom-field conditions (that one
  // toggle governs both), but it's surfaced here as well: it's the difference
  // between "any of these 3 tags" and "all 3 tags", and until now NOTHING outside
  // the collapsed drawer section revealed which mode was active — an AND-filtered
  // list and an OR-filtered list rendered identically.
  //
  // setFilters only (no setFilterDraft) — same as the `assignedTo` toolbar select
  // below: the drawer is a modal `temporary` Drawer, so the toolbar and chip row
  // can't be reached while it's open, and opening it re-seeds filterDraft from
  // filters. Writing both would be a no-op.
  const setTagMatchMode = (mode: 'all' | 'any') =>
    setFilters((prev) => (prev.matchMode === mode ? prev : { ...prev, matchMode: mode }));

  // ── Touch-state segmented toggle (replaces the old "Contacted" dropdown +
  // the 3 separate touch-summary chips with one control) ────────────────────
  // contactedFilter and notesFilter are independently-stored, but never
  // usefully combined — "not contacted" already means no notes, so
  // contacted=no + notes=yes can never match a row. Collapsing them into one
  // exclusive 4-way toggle is a UI simplification only; both URL params still
  // exist underneath exactly as before.
  type TouchFilterValue = 'all' | 'contacted' | 'notContacted' | 'withNotes';
  const touchFilterValue: TouchFilterValue =
    contactedFilter === 'yes'
      ? 'contacted'
      : contactedFilter === 'no'
        ? 'notContacted'
        : notesFilter === 'yes'
          ? 'withNotes'
          : 'all';
  const setTouchFilterValue = (value: TouchFilterValue) =>
    patchQuery({
      contacted: value === 'contacted' ? 'yes' : value === 'notContacted' ? 'no' : 'all',
      notes: value === 'withNotes' ? 'yes' : 'all',
    });

  const activeFilterChips: Array<{
    key: string;
    label: string;
    onRemove: () => void;
    /** Tag chips after the first render the combinator before them — it's both the
     *  only on-screen indicator of the current mode and the control that flips it.
     *  A field on the chip rather than its own array entry, so the Filters-button
     *  badge (activeFilterChips.length) keeps counting actual filters. */
    joinBefore?: boolean;
  }> = [];
  if (statusFilter !== 'all') {
    const opt = STATUS_FILTER_OPTIONS.find((o) => o.value === statusFilter);
    activeFilterChips.push({
      key: 'status',
      label: `Status: ${opt?.label || statusFilter}`,
      onRemove: () => setStatusFilter('all'),
    });
  }
  tagFilter.forEach((tag, i) => {
    activeFilterChips.push({
      key: `tag-${tag}`,
      label: `Tag: ${tag}`,
      joinBefore: i > 0,
      onRemove: () => setTagFilter((prev) => prev.filter((t) => t !== tag)),
    });
  });
  if (isAdvancedFilterSet(filters)) {
    const opt = ADVANCED_FILTER_OPTIONS.find((o) => o.value === filters.advancedFilter);
    activeFilterChips.push({
      key: 'advanced',
      label: opt?.label || filters.advancedFilter,
      onRemove: () =>
        setFilters((prev) => ({
          ...prev,
          advancedFilter: 'all',
          errorCode: '',
          campaignFilter: '',
        })),
    });
  }
  if (filters.source !== 'all') {
    const opt = SOURCE_OPTIONS.find((o) => o.value === filters.source);
    activeFilterChips.push({
      key: 'source',
      label: `Source: ${opt?.label || filters.source}`,
      onRemove: () => setFilters((prev) => ({ ...prev, source: 'all' })),
    });
  }
  if (filters.engagementTier !== 'all') {
    const opt = ENGAGEMENT_TIER_OPTIONS.find((o) => o.value === filters.engagementTier);
    activeFilterChips.push({
      key: 'engagement',
      label: `Engagement: ${opt?.label || filters.engagementTier}`,
      onRemove: () => setFilters((prev) => ({ ...prev, engagementTier: 'all' })),
    });
  }
  if (filters.dateAddedFrom || filters.dateAddedTo) {
    activeFilterChips.push({
      key: 'dateAdded',
      label: `Added ${filters.dateAddedFrom || '…'} → ${filters.dateAddedTo || '…'}`,
      onRemove: () => setFilters((prev) => ({ ...prev, dateAddedFrom: '', dateAddedTo: '' })),
    });
  }
  if (filters.lastMessageFrom || filters.lastMessageTo) {
    activeFilterChips.push({
      key: 'lastMessage',
      label: `Last message ${filters.lastMessageFrom || '…'} → ${filters.lastMessageTo || '…'}`,
      onRemove: () => setFilters((prev) => ({ ...prev, lastMessageFrom: '', lastMessageTo: '' })),
    });
  }
  if (filters.lastNoteFrom || filters.lastNoteTo) {
    activeFilterChips.push({
      key: 'lastNote',
      label: `Last note ${filters.lastNoteFrom || '…'} → ${filters.lastNoteTo || '…'}`,
      onRemove: () => setFilters((prev) => ({ ...prev, lastNoteFrom: '', lastNoteTo: '' })),
    });
  }
  if (filters.campaignSentFrom || filters.campaignSentTo) {
    activeFilterChips.push({
      key: 'campaignSent',
      label: `Campaign sent ${filters.campaignSentFrom || '…'} → ${filters.campaignSentTo || '…'}`,
      onRemove: () => setFilters((prev) => ({ ...prev, campaignSentFrom: '', campaignSentTo: '' })),
    });
  }
  if (filters.frequencyMin || filters.frequencyMax) {
    activeFilterChips.push({
      key: 'frequency',
      label: `Messages ${filters.frequencyMin || '0'}–${filters.frequencyMax || '∞'}`,
      onRemove: () => setFilters((prev) => ({ ...prev, frequencyMin: '', frequencyMax: '' })),
    });
  }
  // "Assigned to" lives as its own quick-select in the toolbar (same tier as
  // Status/Contacted/Tags, which also don't get a chip here) rather than as a
  // chip — see the toolbar render below.
  if (filters.minFailureRate) {
    activeFilterChips.push({
      key: 'qualityRisk',
      label: `Failure rate ≥ ${filters.minFailureRate}%`,
      onRemove: () => setFilters((prev) => ({ ...prev, minFailureRate: '', minAttempts: '' })),
    });
  }
  filters.attributes.forEach((attr, i) => {
    if (attr.key.trim() && attr.value.trim()) {
      activeFilterChips.push({
        key: `attr-${i}`,
        label: `${attr.key} = ${attr.value}`,
        onRemove: () =>
          setFilters((prev) => ({
            ...prev,
            attributes: prev.attributes.filter((_, idx) => idx !== i),
          })),
      });
    }
  });

  return (
    <Box>
      {/* Header actions split into two families: the three you reach for while
          working the list stay labelled, while the display/config actions
          (columns, activity log, assignment rules) collapse to tooltipped icons.
          Six labelled buttons overflowed the row and squeezed the title into a
          one-word column. */}
      <PageHeader
        title="Contacts"
        actions={
          <>
            <UsageChip resource="contacts" label="Contacts" />
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ flexWrap: 'wrap', rowGap: 1 }}
            >
              <Button
                variant="outlined"
                size="small"
                startIcon={<UploadFile />}
                onClick={openImportContacts}
              >
                Import
              </Button>
              <Button variant="outlined" size="small" startIcon={<Add />} onClick={openAddContact}>
                Add Contact
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<FilterList />}
                onClick={(e) => setSegmentsMenuAnchor(e.currentTarget)}
              >
                Segments
              </Button>
              <Divider orientation="vertical" flexItem sx={{ my: 0.75, borderColor: '#e2e8f0' }} />
              <Tooltip title="Table columns">
                <IconButton
                  size="small"
                  aria-label="Table columns"
                  onClick={() => setAttributesOpen(true)}
                  sx={HEADER_ICON_BUTTON_SX}
                >
                  <Settings fontSize="small" />
                </IconButton>
              </Tooltip>
              {isOwnerOrAdmin && (
                <Tooltip title="Activity log">
                  <IconButton
                    size="small"
                    aria-label="Activity log"
                    onClick={openAuditLog}
                    sx={HEADER_ICON_BUTTON_SX}
                  >
                    <HistoryIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {canManageAssignmentRules && (
                <Tooltip title="Assignment rules">
                  <IconButton
                    size="small"
                    aria-label="Assignment rules"
                    onClick={() => setAssignRulesOpen(true)}
                    sx={HEADER_ICON_BUTTON_SX}
                  >
                    <AssignmentInd fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
          </>
        }
      />

      <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <SearchBar
          value={search}
          onChange={setSearch}
          placeholder="Search by name, phone or email…"
          sx={{ minWidth: 260, maxWidth: 360 }}
        />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel id="contacts-status-filter-label">Status</InputLabel>
          <Select
            labelId="contacts-status-filter-label"
            label="Status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            {STATUS_FILTER_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {/* Replaces the old "Contacted" dropdown + 3 separate touch-summary
            chips with one segmented control — same visual pattern as the tag
            match-mode Any/All toggle below, so the toolbar reads as one
            consistent set of controls rather than a dropdown next to a row of
            differently-shaped chips. Counts fill in once touchSummary loads;
            the control itself (and the underlying contacted/notes params) is
            available immediately, same as before. */}
        <Tooltip title="Contacted = a message was exchanged or a note was recorded. Not contacted = neither, ever. With notes = a note exists (the only trace of a call-first, outbound-only workflow).">
          <ToggleButtonGroup
            size="small"
            exclusive
            value={touchFilterValue}
            onChange={(_, v) => setTouchFilterValue((v as TouchFilterValue) ?? 'all')}
            aria-label="Filter by contacted status"
          >
            <ToggleButton value="all" sx={{ px: 1.5, py: 0.5, fontSize: '0.8125rem' }}>
              All
            </ToggleButton>
            <ToggleButton value="contacted" sx={{ px: 1.5, py: 0.5, fontSize: '0.8125rem' }}>
              Contacted{touchSummary ? ` · ${touchSummary.contacted}` : ''}
            </ToggleButton>
            <ToggleButton value="notContacted" sx={{ px: 1.5, py: 0.5, fontSize: '0.8125rem' }}>
              Not contacted{touchSummary ? ` · ${touchSummary.notContacted}` : ''}
            </ToggleButton>
            <ToggleButton value="withNotes" sx={{ px: 1.5, py: 0.5, fontSize: '0.8125rem' }}>
              With notes{touchSummary ? ` · ${touchSummary.withNotes}` : ''}
            </ToggleButton>
          </ToggleButtonGroup>
        </Tooltip>
        <FormControl size="small" sx={{ minWidth: 170 }}>
          <InputLabel id="contacts-assigned-to-filter-label">Assigned to</InputLabel>
          <Select
            labelId="contacts-assigned-to-filter-label"
            label="Assigned to"
            value={filters.assignedTo}
            onChange={(e) => setFilters((prev) => ({ ...prev, assignedTo: e.target.value }))}
          >
            <MenuItem value={ASSIGNED_TO_ALL}>Anyone</MenuItem>
            <MenuItem value={ASSIGNED_TO_UNASSIGNED}>Unassigned</MenuItem>
            {teamMembers.map((m) => (
              <MenuItem key={m._id} value={m._id}>
                {m.firstName} {m.lastName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Autocomplete
          multiple
          size="small"
          options={allTags}
          value={tagFilter}
          onChange={(_, value) => setTagFilter(value)}
          sx={{ minWidth: 240, flexGrow: 1, maxWidth: 420 }}
          renderTags={(value, getTagProps) =>
            value.map((tag, index) => (
              <Chip label={tag} size="small" {...getTagProps({ index })} key={tag} />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...(params as any)}
              label="Tags"
              placeholder={allTags.length ? 'Filter by tag…' : 'No tags yet'}
            />
          )}
        />
        {/* Only meaningful with 2+ tags — one tag matches identically either way,
            and the toolbar is crowded enough that a permanently-visible toggle
            would push the row into wrapping. */}
        {tagFilter.length > 1 && (
          <Tooltip
            title="Any = contacts with at least one of the selected tags. All = only contacts carrying every selected tag."
            arrow
          >
            <ToggleButtonGroup
              size="small"
              exclusive
              value={filters.matchMode}
              onChange={(_, v) => v && setTagMatchMode(v)}
              aria-label="Combine selected tags with"
            >
              <ToggleButton value="any" sx={{ px: 1.25, py: 0.5, fontSize: '0.75rem' }}>
                Any
              </ToggleButton>
              <ToggleButton value="all" sx={{ px: 1.25, py: 0.5, fontSize: '0.75rem' }}>
                All
              </ToggleButton>
            </ToggleButtonGroup>
          </Tooltip>
        )}
        <Button
          variant="outlined"
          size="small"
          startIcon={<Tune sx={{ fontSize: 18 }} />}
          onClick={() => {
            setFilterDraft(filters);
            setFiltersDrawerOpen(true);
          }}
          sx={{ position: 'relative' }}
        >
          Filters
          {activeFilterChips.length > 0 && (
            <Badge
              badgeContent={activeFilterChips.length}
              color="primary"
              sx={{ position: 'absolute', top: 2, right: -6 }}
            />
          )}
        </Button>
        <Box flexGrow={1} />
        <Typography variant="body2" color="text.secondary">
          {contactCountLabel}
        </Typography>
        {canSelectAllFilter && !hasSelection && (
          <Box
            component="span"
            onClick={() => setFilterSelectionMode(true)}
            sx={{
              color: 'primary.main',
              fontSize: '0.8125rem',
              fontWeight: 600,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            Select all {totalCount}
          </Box>
        )}
      </Box>

      {/* ── Active-filter chips — answers "what's currently filtering this list"
           at a glance, without reopening the drawer ─────────────────────────── */}
      {activeFilterChips.length > 0 && (
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          {activeFilterChips.map((chip) => (
            <React.Fragment key={chip.key}>
              {chip.joinBefore && (
                <Tooltip
                  title={
                    filters.matchMode === 'all'
                      ? 'Showing contacts with ALL of these tags. Click for ANY.'
                      : 'Showing contacts with ANY of these tags. Click for ALL.'
                  }
                  arrow
                >
                  <Box
                    component="button"
                    type="button"
                    onClick={() => setTagMatchMode(filters.matchMode === 'all' ? 'any' : 'all')}
                    sx={{
                      border: 'none',
                      background: 'none',
                      p: 0,
                      cursor: 'pointer',
                      font: 'inherit',
                      fontSize: '0.6875rem',
                      fontWeight: 700,
                      letterSpacing: '0.04em',
                      color: filters.matchMode === 'all' ? 'primary.main' : 'text.secondary',
                      '&:hover': { textDecoration: 'underline' },
                    }}
                  >
                    {filters.matchMode === 'all' ? 'AND' : 'OR'}
                  </Box>
                </Tooltip>
              )}
              <Chip
                label={chip.label}
                size="small"
                onDelete={chip.onRemove}
                sx={{ bgcolor: 'primary.50', borderColor: 'primary.200', border: '1px solid' }}
              />
            </React.Fragment>
          ))}
          <Button
            size="small"
            onClick={clearAllContactFilters}
            sx={{ fontSize: '0.75rem' }}
          >
            Clear all
          </Button>
        </Box>
      )}

      {/* ── Filters drawer: every dimension beyond search/status/tags, grouped by
           what the admin is actually asking rather than by build history ─────── */}
      <Drawer anchor="right" open={filtersDrawerOpen} onClose={() => setFiltersDrawerOpen(false)}>
        <Box
          sx={{
            width: 420,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ px: 3, py: 2.5, borderBottom: 1, borderColor: 'divider' }}
          >
            <Typography variant="h6" fontWeight={700}>
              Filters
            </Typography>
            <IconButton size="small" onClick={() => setFiltersDrawerOpen(false)}>
              <Close fontSize="small" />
            </IconButton>
          </Stack>

          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              px: 3,
              py: 3,
              display: 'flex',
              flexDirection: 'column',
              gap: 3.5,
            }}
          >
            <FilterSection
              title="Delivery status"
              icon={<Send sx={{ fontSize: 20 }} />}
              helper='Based on each contact&apos;s most recent campaign send. Different from "Engagement" below, which looks at any inbound message regardless of campaigns.'
              defaultExpanded={isAdvancedFilterSet(filterDraft)}
            >
              <FormControl size="small" fullWidth>
                <InputLabel id="filter-advanced-label">WhatsApp status</InputLabel>
                <Select
                  labelId="filter-advanced-label"
                  label="WhatsApp status"
                  value={filterDraft.advancedFilter}
                  onChange={(e) => {
                    const next = e.target.value;
                    setFilterDraft((prev) => ({
                      ...prev,
                      advancedFilter: next,
                      ...(next !== 'failedByError' ? { errorCode: '' } : {}),
                      ...(next !== 'neverDeliveredThisCampaign' ? { campaignFilter: '' } : {}),
                    }));
                  }}
                >
                  {ADVANCED_FILTER_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {filterDraft.advancedFilter === 'failedByError' && (
                <TextField
                  size="small"
                  label="Error code"
                  placeholder="e.g. 131026"
                  value={filterDraft.errorCode}
                  onChange={(e) =>
                    setFilterDraft((prev) => ({
                      ...prev,
                      errorCode: e.target.value.replace(/[^0-9]/g, ''),
                    }))
                  }
                  inputProps={{ inputMode: 'numeric' }}
                  fullWidth
                />
              )}
              {filterDraft.advancedFilter === 'neverDeliveredThisCampaign' && (
                <FormControl size="small" fullWidth>
                  <InputLabel id="filter-campaign-label" shrink>
                    Campaign
                  </InputLabel>
                  <Select
                    labelId="filter-campaign-label"
                    label="Campaign"
                    notched
                    value={filterDraft.campaignFilter}
                    displayEmpty
                    onChange={(e) =>
                      setFilterDraft((prev) => ({ ...prev, campaignFilter: e.target.value }))
                    }
                  >
                    <MenuItem value="">
                      <em>{campaigns.length ? 'Select a campaign…' : 'No campaigns yet'}</em>
                    </MenuItem>
                    {campaigns.map((c) => (
                      <MenuItem key={c._id} value={c._id}>
                        {c.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              {(filterDraft.advancedFilter === 'neverDeliveredThisCampaign' ||
                filterDraft.advancedFilter === 'neverDeliveredAnyCampaign') && (
                <Typography variant="caption" color="text.disabled">
                  {filterDraft.advancedFilter === 'neverDeliveredThisCampaign'
                    ? 'Contacts this campaign tried to reach but never delivered to — transient, may still succeed on a retry or a future run. Excludes contacts this campaign never targeted.'
                    : 'Contacts targeted by at least one campaign whose every attempt failed — a strong signal the number is permanently unreachable. Excludes contacts never targeted at all — use “Never targeted” for those.'}
                </Typography>
              )}
              {filterDraft.advancedFilter === 'neverTargetedAnyCampaign' && (
                <Typography variant="caption" color="text.disabled">
                  Contacts no campaign has ever tried to reach (zero send history) — your fresh,
                  un-messaged audience. Not the same as “Never delivered”, which needs at least one
                  failed attempt to exist.
                </Typography>
              )}
            </FilterSection>

            <Divider />

            <FilterSection
              title="Engagement recency"
              icon={<TrendingUp sx={{ fontSize: 20 }} />}
              helper="Based on the contact's most recent inbound message, regardless of which campaign (if any) prompted it — not tied to one specific send like Delivery status above."
              defaultExpanded={filterDraft.engagementTier !== 'all'}
            >
              <FormControl size="small" fullWidth>
                <InputLabel id="filter-engagement-label">Engagement</InputLabel>
                <Select
                  labelId="filter-engagement-label"
                  label="Engagement"
                  value={filterDraft.engagementTier}
                  onChange={(e) =>
                    setFilterDraft((prev) => ({ ...prev, engagementTier: e.target.value }))
                  }
                >
                  {ENGAGEMENT_TIER_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </FilterSection>

            <Divider />

            <FilterSection
              title="Source & timing"
              icon={<Schedule sx={{ fontSize: 20 }} />}
              defaultExpanded={
                filterDraft.source !== 'all' ||
                Boolean(filterDraft.dateAddedFrom) ||
                Boolean(filterDraft.dateAddedTo) ||
                Boolean(filterDraft.lastMessageFrom) ||
                Boolean(filterDraft.lastMessageTo) ||
                Boolean(filterDraft.lastNoteFrom) ||
                Boolean(filterDraft.lastNoteTo) ||
                Boolean(filterDraft.campaignSentFrom) ||
                Boolean(filterDraft.campaignSentTo) ||
                Boolean(filterDraft.frequencyMin) ||
                Boolean(filterDraft.frequencyMax)
              }
            >
              <FormControl size="small" fullWidth>
                <InputLabel id="filter-source-label">Source</InputLabel>
                <Select
                  labelId="filter-source-label"
                  label="Source"
                  value={filterDraft.source}
                  onChange={(e) => setFilterDraft((prev) => ({ ...prev, source: e.target.value }))}
                >
                  {SOURCE_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <DateRangeFilter
                label="Date added"
                from={filterDraft.dateAddedFrom}
                to={filterDraft.dateAddedTo}
                onChange={(r) =>
                  setFilterDraft((prev) => ({ ...prev, dateAddedFrom: r.from, dateAddedTo: r.to }))
                }
              />

              <DateRangeFilter
                label="Last message"
                helper="Any message sent or received (either direction)."
                from={filterDraft.lastMessageFrom}
                to={filterDraft.lastMessageTo}
                onChange={(r) =>
                  setFilterDraft((prev) => ({
                    ...prev,
                    lastMessageFrom: r.from,
                    lastMessageTo: r.to,
                  }))
                }
              />

              <DateRangeFilter
                label="Last note added"
                helper="Notes your team typed in the app (imported notes excluded)."
                from={filterDraft.lastNoteFrom}
                to={filterDraft.lastNoteTo}
                onChange={(r) =>
                  setFilterDraft((prev) => ({ ...prev, lastNoteFrom: r.from, lastNoteTo: r.to }))
                }
              />

              <DateRangeFilter
                label="Campaign sent"
                from={filterDraft.campaignSentFrom}
                to={filterDraft.campaignSentTo}
                onChange={(r) =>
                  setFilterDraft((prev) => ({
                    ...prev,
                    campaignSentFrom: r.from,
                    campaignSentTo: r.to,
                  }))
                }
              />

              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mb: 0.5 }}
                >
                  Message count (frequency)
                </Typography>
                <Stack direction="row" spacing={1}>
                  <TextField
                    size="small"
                    label="Min"
                    inputProps={{ inputMode: 'numeric' }}
                    value={filterDraft.frequencyMin}
                    onChange={(e) =>
                      setFilterDraft((prev) => ({
                        ...prev,
                        frequencyMin: e.target.value.replace(/[^0-9]/g, ''),
                      }))
                    }
                    fullWidth
                  />
                  <TextField
                    size="small"
                    label="Max"
                    inputProps={{ inputMode: 'numeric' }}
                    value={filterDraft.frequencyMax}
                    onChange={(e) =>
                      setFilterDraft((prev) => ({
                        ...prev,
                        frequencyMax: e.target.value.replace(/[^0-9]/g, ''),
                      }))
                    }
                    fullWidth
                  />
                </Stack>
              </Box>
            </FilterSection>

            <Divider />

            <FilterSection
              title="Delivery risk"
              icon={<ErrorOutline sx={{ fontSize: 20 }} />}
              helper="A proxy computed from this contact's own send history in your campaigns — how often their sends have failed. This is NOT Meta's official phone-number quality rating, which is a separate, account-wide metric set by WhatsApp and isn't tracked per-contact here."
              defaultExpanded={Boolean(filterDraft.minFailureRate)}
            >
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mb: 0.5 }}
                >
                  Campaign send-failure rate
                </Typography>
                <Stack direction="row" spacing={1}>
                  <TextField
                    size="small"
                    label="Min failure rate %"
                    inputProps={{ inputMode: 'decimal' }}
                    value={filterDraft.minFailureRate}
                    onChange={(e) =>
                      setFilterDraft((prev) => ({
                        ...prev,
                        minFailureRate: e.target.value.replace(/[^0-9.]/g, ''),
                      }))
                    }
                    fullWidth
                  />
                  <TextField
                    size="small"
                    label="Min attempts"
                    placeholder="3"
                    inputProps={{ inputMode: 'numeric' }}
                    value={filterDraft.minAttempts}
                    onChange={(e) =>
                      setFilterDraft((prev) => ({
                        ...prev,
                        minAttempts: e.target.value.replace(/[^0-9]/g, ''),
                      }))
                    }
                    fullWidth
                  />
                </Stack>
                <Typography
                  variant="caption"
                  color="text.disabled"
                  sx={{ display: 'block', mt: 0.5 }}
                >
                  Only applies once "min failure rate" is set. Min attempts defaults to 3 — a
                  contact needs at least that many campaign sends before a failure rate means
                  anything (1 failed out of 1 attempt isn't a meaningful risk signal).
                </Typography>
              </Box>
            </FilterSection>

            <Divider />

            <FilterSection
              title="Tags & custom fields"
              icon={<LocalOffer sx={{ fontSize: 20 }} />}
              // Also expand on 2+ tags: this section owns the tag combinator, so
              // leaving it collapsed for a tags-only filter hid the one control
              // that decides whether the list is a union or an intersection.
              defaultExpanded={
                tagFilter.length > 1 ||
                filterDraft.attributes.some((a) => a.key.trim() && a.value.trim())
              }
            >
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography variant="caption" color="text.secondary">
                  Match tags + custom-field conditions (same toggle as the tag bar)
                </Typography>
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={filterDraft.matchMode}
                  onChange={(_, v) => v && setFilterDraft((prev) => ({ ...prev, matchMode: v }))}
                >
                  <ToggleButton value="any" sx={{ px: 1.5, py: 0.25, fontSize: '0.75rem' }}>
                    Any
                  </ToggleButton>
                  <ToggleButton value="all" sx={{ px: 1.5, py: 0.25, fontSize: '0.75rem' }}>
                    All
                  </ToggleButton>
                </ToggleButtonGroup>
              </Stack>

              {filterDraft.attributes.map((attr, i) => (
                <Stack direction="row" spacing={1} key={i}>
                  <Autocomplete
                    size="small"
                    freeSolo
                    options={attributeKeys}
                    inputValue={attr.key}
                    onInputChange={(_, v) =>
                      setFilterDraft((prev) => ({
                        ...prev,
                        attributes: prev.attributes.map((a, idx) =>
                          idx === i ? { ...a, key: v } : a
                        ),
                      }))
                    }
                    sx={{ flex: 1 }}
                    renderInput={(params) => <TextField {...(params as any)} label="Field" />}
                  />
                  <TextField
                    size="small"
                    label="Value"
                    value={attr.value}
                    onChange={(e) =>
                      setFilterDraft((prev) => ({
                        ...prev,
                        attributes: prev.attributes.map((a, idx) =>
                          idx === i ? { ...a, value: e.target.value } : a
                        ),
                      }))
                    }
                    sx={{ flex: 1 }}
                  />
                  <IconButton
                    size="small"
                    onClick={() =>
                      setFilterDraft((prev) => ({
                        ...prev,
                        attributes: prev.attributes.filter((_, idx) => idx !== i),
                      }))
                    }
                  >
                    <Close fontSize="small" />
                  </IconButton>
                </Stack>
              ))}
              <Button
                size="small"
                onClick={() =>
                  setFilterDraft((prev) => ({
                    ...prev,
                    attributes: [...prev.attributes, { key: '', value: '' }],
                  }))
                }
                sx={{ alignSelf: 'flex-start' }}
              >
                + Add custom-field condition
              </Button>
            </FilterSection>
          </Box>

          <Stack
            direction="row"
            spacing={1}
            justifyContent="flex-end"
            sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider' }}
          >
            <Button
              size="small"
              onClick={() => {
                clearAllContactFilters();
                setFiltersDrawerOpen(false);
              }}
            >
              Clear all
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={() => {
                setFilters(filterDraft);
                setFiltersDrawerOpen(false);
              }}
            >
              Apply
            </Button>
          </Stack>
        </Box>
      </Drawer>

      {/* ── Bulk action bar ──────────────────────────────────────────────────── */}
      {hasSelection && (
        <Box
          sx={{
            mb: 1.5,
            px: 2,
            py: 1,
            borderRadius: 1,
            bgcolor: 'primary.50',
            border: '1px solid',
            borderColor: 'primary.200',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <Typography variant="body2" sx={{ flexGrow: 1 }}>
            <Box component="span" fontWeight={600}>
              {selectionCount} selected
            </Box>
            {canSelectAllFilter && (
              <>
                {' · '}
                <Box
                  component="span"
                  onClick={() => setFilterSelectionMode(true)}
                  sx={{
                    color: 'primary.main',
                    fontWeight: 600,
                    cursor: 'pointer',
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  Select all {totalCount}
                </Box>
              </>
            )}
          </Typography>
          <Divider orientation="vertical" flexItem sx={{ my: 0.5 }} />
          <Tooltip
            title={
              filterSelectionMode && !isOwner
                ? 'Only the tenant owner can bulk-reassign contacts by filter'
                : ''
            }
          >
            <span>
              <Button
                size="small"
                variant="outlined"
                startIcon={<AssignmentInd />}
                onClick={() => {
                  if (filterSelectionMode) {
                    setOwnerBulkAssignUserId('');
                    setOwnerBulkAssignConsent(false);
                    setOwnerBulkAssignOpen(true);
                  } else {
                    setBulkAssignUserId('');
                    setBulkAssignOpen(true);
                  }
                }}
                disabled={
                  bulkSaving ||
                  !canAssignContacts ||
                  (filterSelectionMode ? !isOwner : selectedIds.size === 0)
                }
              >
                Assign to…
              </Button>
            </span>
          </Tooltip>
          <Tooltip
            title={
              filterSelectionMode && !isOwner
                ? 'Only the tenant owner can bulk-unassign contacts by filter'
                : !canAssignContacts
                  ? 'You need Full access to Chat Logs to unassign contacts'
                  : ''
            }
          >
            <span>
              <Button
                size="small"
                variant="outlined"
                startIcon={<PersonRemove />}
                onClick={() => {
                  if (filterSelectionMode) {
                    setOwnerBulkUnassignApplyRules(true);
                    setOwnerBulkUnassignConsent(false);
                    setOwnerBulkUnassignOpen(true);
                  } else {
                    setUnassignApplyRules(true);
                    setUnassignPendingIds(Array.from(selectedIds));
                  }
                }}
                disabled={
                  bulkSaving ||
                  !canAssignContacts ||
                  (filterSelectionMode ? !isOwner : selectedIds.size === 0)
                }
              >
                Unassign
              </Button>
            </span>
          </Tooltip>
          <Tooltip
            title={
              filterSelectionMode && !isOwnerOrAdmin
                ? 'Only the tenant owner or an admin can bulk-edit tags by filter'
                : ''
            }
          >
            <span>
              <Button
                size="small"
                variant="outlined"
                startIcon={<LocalOffer sx={{ fontSize: 18 }} />}
                onClick={() => openBulkTags('add')}
                disabled={
                  bulkSaving || (filterSelectionMode ? !isOwnerOrAdmin : selectedIds.size === 0)
                }
              >
                Add tags…
              </Button>
            </span>
          </Tooltip>
          <Tooltip
            title={
              filterSelectionMode && !isOwnerOrAdmin
                ? 'Only the tenant owner or an admin can bulk-edit tags by filter'
                : ''
            }
          >
            <span>
              <Button
                size="small"
                variant="outlined"
                startIcon={<Block sx={{ fontSize: 18 }} />}
                onClick={() => openBulkTags('remove')}
                disabled={
                  bulkSaving || (filterSelectionMode ? !isOwnerOrAdmin : selectedIds.size === 0)
                }
              >
                Remove tags…
              </Button>
            </span>
          </Tooltip>
          <Button
            size="small"
            variant="outlined"
            startIcon={<ArchiveIcon />}
            onClick={() => setBulkArchiveConfirmOpen(true)}
            disabled={bulkSaving}
          >
            Archive
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="error"
            startIcon={<DeleteOutline />}
            onClick={() => setBulkDeleteConfirmOpen(true)}
            disabled={bulkSaving}
          >
            Delete
          </Button>
          <IconButton size="small" onClick={clearSelection} disabled={bulkSaving}>
            <Close fontSize="small" />
          </IconButton>
        </Box>
      )}

      <Card>
        <CardContent sx={{ p: contacts.length ? '0 !important' : undefined }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress size={26} />
            </Box>
          ) : contacts.length === 0 ? (
            <EmptyState
              icon={<ContactsIcon />}
              title="No contacts yet"
              description="Contacts from your conversations will appear here. You can also add them via the API."
            />
          ) : (
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table
                sx={{
                  // The column set is wide by design (contacts carry a lot of
                  // context), so scroll horizontally rather than let headers
                  // wrap onto two lines — a wrapped header row reads as if the
                  // columns and the data below have come out of alignment. Any
                  // column can be switched off in Table Columns.
                  '& thead th': { whiteSpace: 'nowrap' },
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        indeterminate={!filterSelectionMode && somePageSelected && !allPageSelected}
                        checked={allPageSelected || filterSelectionMode}
                        onChange={toggleAllPage}
                      />
                    </TableCell>
                    <TableCell>Contact</TableCell>
                    {visibleFields.email && <TableCell>Email</TableCell>}
                    {visibleFields.country && <TableCell>Country</TableCell>}
                    {visibleFields.tags && <TableCell>Tags</TableCell>}
                    {visibleFields.source && <TableCell>Source</TableCell>}
                    {visibleFields.customFields && <TableCell>Custom Fields</TableCell>}
                    {visibleFields.conversation && <TableCell>Conversation</TableCell>}
                    {visibleFields.date && <TableCell>Last Contact</TableCell>}
                    {visibleFields.lastNote && <TableCell>Last Note</TableCell>}
                    {visibleFields.status && <TableCell>Status</TableCell>}
                    {visibleFields.owner && <TableCell>Assigned To</TableCell>}
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {contacts.map((contact) => (
                    <TableRow
                      key={contact._id}
                      hover
                      selected={selectedIds.has(contact._id) || filterSelectionMode}
                      onClick={() => openChatLogs(contact)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell padding="checkbox" onClick={(e) => toggleRow(contact._id, e)}>
                        <Checkbox
                          size="small"
                          checked={selectedIds.has(contact._id) || filterSelectionMode}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Avatar
                            sx={{
                              width: 34,
                              height: 34,
                              bgcolor: alpha('#4f46e5', 0.12),
                              color: '#4f46e5',
                              fontSize: '0.8125rem',
                              fontWeight: 700,
                            }}
                          >
                            {(
                              contact.customerName?.[0] ||
                              contact.customerPhone?.[0] ||
                              '?'
                            ).toUpperCase()}
                          </Avatar>
                          <Box>
                            <Typography
                              variant="body2"
                              fontWeight={600}
                              onClick={(e) => {
                                // The row itself opens chat logs — stop that here so
                                // the name means "open the contact record" instead.
                                e.stopPropagation();
                                openContactDetail(contact);
                              }}
                              sx={{
                                cursor: 'pointer',
                                color: 'primary.main',
                                '&:hover': { textDecoration: 'underline' },
                              }}
                            >
                              {contact.customerName || 'Unknown'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatPhoneDisplay(contact.customerPhone)}
                            </Typography>
                            {isNewlyAssignedToMe(contact) && (
                              <Tooltip title="Assigned to you — you haven't opened it since">
                                <Chip
                                  label="New"
                                  size="small"
                                  color="primary"
                                  sx={{
                                    mt: 0.25,
                                    height: 18,
                                    fontSize: '0.65rem',
                                    width: 'fit-content',
                                  }}
                                />
                              </Tooltip>
                            )}
                            {!contact.notContacted && (contact.noteCount || 0) > 0 && (
                              <Tooltip
                                title={`${contact.noteCount} note${contact.noteCount === 1 ? '' : 's'} recorded`}
                              >
                                <Chip
                                  label={`${contact.noteCount} note${contact.noteCount === 1 ? '' : 's'}`}
                                  size="small"
                                  variant="outlined"
                                  sx={{ mt: 0.25, height: 18, fontSize: '0.65rem', width: 'fit-content' }}
                                />
                              </Tooltip>
                            )}
                            {contact.notContacted && (
                              <Tooltip title="No messages exchanged and no notes recorded yet">
                                <Chip
                                  label="Not contacted"
                                  size="small"
                                  variant="outlined"
                                  color="warning"
                                  sx={{
                                    mt: 0.25,
                                    height: 18,
                                    fontSize: '0.65rem',
                                    width: 'fit-content',
                                  }}
                                />
                              </Tooltip>
                            )}
                          </Box>
                        </Box>
                      </TableCell>
                      {visibleFields.email && (
                        <TableCell>
                          {contact.customerEmail ? (
                            <Tooltip title={contact.customerEmail}>
                              <Typography variant="body2" noWrap sx={{ maxWidth: 170 }}>
                                {contact.customerEmail}
                              </Typography>
                            </Tooltip>
                          ) : (
                            '—'
                          )}
                        </TableCell>
                      )}
                      {visibleFields.country && (
                        <TableCell>
                          {contact.customerCountryCode || contact.customerCountry || '—'}
                        </TableCell>
                      )}
                      {visibleFields.tags && (
                        <TableCell>
                          {contact.tags && contact.tags.length > 0 ? (
                            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                              {contact.tags.map((tag) => (
                                <Chip key={tag} label={tag} size="small" variant="outlined" />
                              ))}
                            </Stack>
                          ) : (
                            '—'
                          )}
                        </TableCell>
                      )}
                      {visibleFields.source && (
                        <TableCell>
                          {contact.source ? (
                            <Tooltip
                              title={contact.sourceDetail ? `Channel: ${contact.source}` : ''}
                              disableHoverListener={!contact.sourceDetail}
                            >
                              <Stack direction="row" spacing={0.75} alignItems="center">
                                <Box sx={{ display: 'flex', color: 'text.secondary' }}>
                                  {SOURCE_META[contact.source]?.icon || (
                                    <InfoOutlined sx={{ fontSize: 16 }} />
                                  )}
                                </Box>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  noWrap
                                  sx={{
                                    textTransform: contact.sourceDetail ? 'none' : 'capitalize',
                                  }}
                                >
                                  {contact.sourceDetail ||
                                    SOURCE_META[contact.source]?.label ||
                                    contact.source}
                                </Typography>
                              </Stack>
                            </Tooltip>
                          ) : (
                            '—'
                          )}
                        </TableCell>
                      )}
                      {visibleFields.customFields && (
                        <TableCell>
                          {contact.customAttributes && contact.customAttributes.length > 0 ? (
                            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                              {contact.customAttributes.slice(0, 3).map((attr) => (
                                <Tooltip key={attr.key} title={`${attr.key}: ${attr.value}`}>
                                  <Chip
                                    label={`${attr.key}: ${attr.value}`}
                                    size="small"
                                    variant="outlined"
                                    sx={{ maxWidth: 160 }}
                                  />
                                </Tooltip>
                              ))}
                              {contact.customAttributes.length > 3 && (
                                <Chip
                                  label={`+${contact.customAttributes.length - 3}`}
                                  size="small"
                                />
                              )}
                            </Stack>
                          ) : (
                            '—'
                          )}
                        </TableCell>
                      )}
                      {visibleFields.conversation && (
                        <TableCell>
                          <Tooltip title={contact.lastMessage || 'No message'}>
                            <Box>
                              <Typography variant="body2" noWrap sx={{ maxWidth: 260 }}>
                                {contact.lastMessage || 'No message yet'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {contact.messageCount || 0} msgs
                                {contact.lastMessageAt
                                  ? ` • ${new Date(contact.lastMessageAt).toLocaleString()}`
                                  : ''}
                              </Typography>
                            </Box>
                          </Tooltip>
                        </TableCell>
                      )}
                      {visibleFields.date && (
                        <TableCell>
                          {contact.lastContactAt ? (
                            <Box>
                              <Typography variant="body2" noWrap>
                                {new Date(contact.lastContactAt).toLocaleDateString()}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" noWrap>
                                {new Date(contact.lastContactAt).toLocaleTimeString([], {
                                  hour: '2-digit',
                                  minute: '2-digit',
                                })}
                              </Typography>
                            </Box>
                          ) : (
                            '—'
                          )}
                        </TableCell>
                      )}
                      {visibleFields.lastNote && (
                        <TableCell>
                          {contact.lastNote ? (
                            <Tooltip
                              title={`${contact.lastNote.text} — ${contact.lastNote.authorName}${
                                contact.lastNote.createdAt
                                  ? ` · ${new Date(contact.lastNote.createdAt).toLocaleString()}`
                                  : ''
                              }`}
                            >
                              <Box sx={{ maxWidth: 230 }}>
                                <Typography variant="body2" noWrap>
                                  {contact.lastNote.text}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" noWrap>
                                  {contact.lastNote.authorName}
                                  {contact.lastNote.createdAt
                                    ? ` · ${new Date(contact.lastNote.createdAt).toLocaleDateString()}`
                                    : ''}
                                  {(contact.noteCount || 0) > 1
                                    ? ` · +${(contact.noteCount || 0) - 1} more`
                                    : ''}
                                </Typography>
                              </Box>
                            </Tooltip>
                          ) : (
                            <Typography variant="body2" color="text.disabled">
                              —
                            </Typography>
                          )}
                        </TableCell>
                      )}
                      {visibleFields.status && (
                        <TableCell>
                          <StatusChip
                            status={contact.status}
                            dot
                            sx={{ textTransform: 'capitalize' }}
                          />
                        </TableCell>
                      )}
                      {visibleFields.owner && (
                        <TableCell>
                          {contact.assignedTo
                            ? `${contact.assignedTo.firstName || ''} ${contact.assignedTo.lastName || ''}`.trim() ||
                              contact.assignedTo.email ||
                              '—'
                            : '—'}
                        </TableCell>
                      )}
                      <TableCell align="right">
                        <Tooltip title="View contact">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              openContactDetail(contact);
                            }}
                          >
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <IconButton
                          size="small"
                          onClick={(e) => openActionMenu(e, contact)}
                          disabled={statusSavingId === contact._id}
                        >
                          {statusSavingId === contact._id ? (
                            <CircularProgress size={16} />
                          ) : (
                            <MoreVert fontSize="small" />
                          )}
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {!loading && totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2.5 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, value) => setPage(value)}
            color="primary"
            size="small"
          />
        </Box>
      )}

      <Menu anchorEl={actionMenuAnchor} open={Boolean(actionMenuAnchor)} onClose={closeActionMenu}>
        <MenuItem
          onClick={() => {
            if (actionMenuContact) openContactDetail(actionMenuContact);
            closeActionMenu();
          }}
        >
          <ListItemIcon>
            <Visibility fontSize="small" />
          </ListItemIcon>
          <ListItemText>View contact</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (actionMenuContact) openEditContact(actionMenuContact);
            closeActionMenu();
          }}
        >
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit contact</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (actionMenuContact) {
              setQuickAssignContact(actionMenuContact);
              setQuickAssignUserId('');
            }
            closeActionMenu();
          }}
        >
          <ListItemIcon>
            <AssignmentInd fontSize="small" />
          </ListItemIcon>
          <ListItemText>Assign…</ListItemText>
        </MenuItem>
        {actionMenuContact?.assignedTo && (
          <MenuItem
            onClick={() => {
              setUnassignApplyRules(true);
              setUnassignPendingIds(actionMenuContact ? [actionMenuContact._id] : []);
              closeActionMenu();
            }}
          >
            <ListItemIcon>
              <PersonRemove fontSize="small" />
            </ListItemIcon>
            <ListItemText>Unassign</ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={handleToggleStatus}>
          <ListItemIcon>
            {actionMenuContact?.status === 'archived' ? (
              <CheckCircleOutline fontSize="small" />
            ) : (
              <Block fontSize="small" />
            )}
          </ListItemIcon>
          <ListItemText>
            {actionMenuContact?.status === 'archived' ? 'Activate contact' : 'Deactivate contact'}
          </ListItemText>
        </MenuItem>
        <MenuItem onClick={openDeleteConfirm} sx={{ color: 'error.main' }}>
          <ListItemIcon sx={{ color: 'error.main' }}>
            <DeleteOutline fontSize="small" />
          </ListItemIcon>
          <ListItemText>Delete contact…</ListItemText>
        </MenuItem>
      </Menu>

      <Dialog open={Boolean(deleteTarget)} onClose={() => !deleteSaving && setDeleteTarget(null)}>
        <DialogTitle>Delete Contact</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This permanently deletes{' '}
            <strong>
              {deleteTarget?.customerName || formatPhoneDisplay(deleteTarget?.customerPhone || '')}
            </strong>{' '}
            and its message history. This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleteSaving}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteContact}
            variant="contained"
            color="error"
            disabled={deleteSaving}
          >
            {deleteSaving ? <CircularProgress size={18} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(actionError)}
        autoHideDuration={5000}
        onClose={() => setActionError('')}
      >
        <Alert severity="error" onClose={() => setActionError('')}>
          {actionError}
        </Alert>
      </Snackbar>

      {/* ── Contact activity log ──────────────────────────────────────────
           The tenant-side view of AuditEvent rows. Bulk operations write one
           summary row carrying no resourceId, so they show up here and
           nowhere else in this app — an individual contact's timeline can
           only ever match rows that name that contact. ──────────────────── */}
      <Dialog open={auditLogOpen} onClose={() => setAuditLogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <span>Contact activity log</span>
            <FormControlLabel
              sx={{ mr: 0 }}
              control={
                <Checkbox
                  size="small"
                  checked={auditBulkOnly}
                  onChange={(e) => {
                    setAuditBulkOnly(e.target.checked);
                    void fetchAuditLog(1, e.target.checked);
                  }}
                />
              }
              label={<Typography variant="body2">Bulk operations only</Typography>}
            />
          </Stack>
          <Typography variant="caption" color="text.secondary">
            Who created, edited, archived or deleted contacts — and when. Each bulk action is one
            summary row with the count and the filter it ran on.
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {auditError && (
            <Alert severity="error" sx={{ mb: 1.5 }}>
              {auditError}
            </Alert>
          )}
          {auditLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress size={28} />
            </Box>
          ) : auditRows.length === 0 && !auditError ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
              {auditBulkOnly
                ? 'No bulk operations recorded yet.'
                : 'No contact activity recorded yet.'}
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {/* Only Detail is allowed to wrap — When/Action/By each held a
                        single short value that was being broken across two lines,
                        which is what made the rows uneven. */}
                    <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap', width: 180 }}>
                      When
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap', width: 150 }}>
                      Action
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Detail</TableCell>
                    <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap', width: 160 }}>By</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {auditRows.map((e) => (
                    <TableRow key={e._id} hover>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Typography variant="body2">
                          {new Date(e.createdAt).toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <StatusChip
                            status={AUDIT_ACTION_STATUS[e.action] || 'inactive'}
                            label={e.action}
                          />
                          {/* 'bulk' is a qualifier, not a second status — kept
                              deliberately quiet so the action stays the thing
                              the eye lands on. */}
                          {e.isBulk && <StatusChip status="inactive" label="bulk" />}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        {e.isBulk ? (
                          <Typography variant="body2">
                            <strong>
                              {e.affectedCount} contact{e.affectedCount === 1 ? '' : 's'}
                            </strong>
                            {e.reason ? ` — ${e.reason}` : ''}
                          </Typography>
                        ) : e.changedKeys && e.changedKeys.length > 0 ? (
                          <Box>
                            {e.changedKeys.slice(0, 3).map((c) => (
                              <Typography key={c.key} variant="body2">
                                {c.key}: {formatAuditValue(c.from)} → {formatAuditValue(c.to)}
                              </Typography>
                            ))}
                            {e.changedKeys.length > 3 && (
                              <Typography variant="caption" color="text.secondary">
                                +{e.changedKeys.length - 3} more field
                                {e.changedKeys.length - 3 === 1 ? '' : 's'}
                              </Typography>
                            )}
                          </Box>
                        ) : (
                          <Typography variant="body2">{e.resourceName || '—'}</Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Typography variant="body2">{e.actorName}</Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between' }}>
          {auditTotal > AUDIT_PAGE_SIZE ? (
            <Pagination
              size="small"
              count={Math.ceil(auditTotal / AUDIT_PAGE_SIZE)}
              page={auditPage}
              onChange={(_, p) => void fetchAuditLog(p, auditBulkOnly)}
              disabled={auditLoading}
            />
          ) : (
            <Box />
          )}
          <Button onClick={() => setAuditLogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ── Bulk add/remove tags ───────────────────────────────────────────
           Additive/subtractive only — no "replace tags" mode exists, by
           design: a bulk overwrite is the operation that makes a mis-click
           unrecoverable. ─────────────────────────────────────────────────── */}
      <Dialog
        open={bulkTagsOpen}
        onClose={() => !bulkSaving && setBulkTagsOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {bulkTagsOperation === 'add' ? 'Add tags to contacts' : 'Remove tags from contacts'}
        </DialogTitle>
        <DialogContent>
          {bulkActionError && (
            <Alert severity="error" sx={{ mb: 1.5 }}>
              {bulkActionError}
            </Alert>
          )}
          <Alert
            severity={bulkTagsOperation === 'add' ? 'info' : 'warning'}
            sx={{ mb: 2 }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              {filterSelectionMode
                ? `All ${totalCount} contacts ${describeActiveFilters()}`
                : `${selectionCount} selected contact${selectionCount === 1 ? '' : 's'}`}
            </Typography>
            <Typography variant="body2">
              {bulkTagsOperation === 'add'
                ? 'Existing tags are kept — these are added on top. Contacts that already have a tag are left unchanged, and tags are capped at 30 per contact.'
                : 'Only the tags you pick are removed; every other tag on these contacts is kept. Tags drive assignment rules and campaign targeting, so removing one can change who a contact routes to and which campaigns reach it.'}
            </Typography>
          </Alert>

          <Autocomplete
            multiple
            freeSolo={bulkTagsOperation === 'add'}
            size="small"
            options={allTags}
            value={bulkTagsValues}
            onChange={(_, value) => setBulkTagsValues(cleanTags(value as string[]))}
            renderTags={(value, getTagProps) =>
              value.map((tag, index) => (
                <Chip label={tag} size="small" {...getTagProps({ index })} key={tag} />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...(params as any)}
                autoFocus
                label={bulkTagsOperation === 'add' ? 'Tags to add' : 'Tags to remove'}
                placeholder={
                  bulkTagsOperation === 'add' ? 'Pick or type a new tag…' : 'Pick existing tags…'
                }
                helperText={
                  bulkTagsOperation === 'add'
                    ? 'Type a new tag and press Enter to create it.'
                    : 'Matching is case-insensitive, so a legacy "Buyer" is removed by "buyer" too.'
                }
              />
            )}
          />

          {filterSelectionMode && (
            <FormControlLabel
              sx={{ mt: 1.5, alignItems: 'flex-start' }}
              control={
                <Checkbox
                  size="small"
                  checked={bulkTagsConsent}
                  onChange={(e) => setBulkTagsConsent(e.target.checked)}
                />
              }
              label={
                <Typography variant="body2">
                  I understand this runs on <strong>every contact matching the filter</strong> —
                  {' '}{totalCount} contact{totalCount === 1 ? '' : 's'}, including ones not on
                  this page.
                </Typography>
              }
            />
          )}

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
            Recorded in the activity log with the tags and the filter used. Assignment rules are
            not re-run — use "Apply rules now" on the Assignment rules dialog if you want that.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkTagsOpen(false)} disabled={bulkSaving}>
            Cancel
          </Button>
          <Button
            onClick={handleBulkTags}
            variant="contained"
            // Both actions use the app's primary blue. The amber panel above
            // already carries the "removing a tag changes routing" caution —
            // an orange CTA on top of it read as destructive, which a
            // reversible tag edit is not.
            color="primary"
            disabled={
              bulkSaving ||
              bulkTagsValues.length === 0 ||
              (filterSelectionMode && !bulkTagsConsent)
            }
          >
            {bulkSaving ? (
              <CircularProgress size={18} />
            ) : bulkTagsOperation === 'add' ? (
              'Add tags'
            ) : (
              'Remove tags'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Bulk archive confirm ──────────────────────────────────────────── */}
      <Dialog
        open={bulkArchiveConfirmOpen}
        onClose={() => !bulkSaving && setBulkArchiveConfirmOpen(false)}
      >
        <DialogTitle>Archive Contacts</DialogTitle>
        <DialogContent>
          {bulkActionError && (
            <Alert severity="error" sx={{ mb: 1.5 }}>
              {bulkActionError}
            </Alert>
          )}
          <Alert severity="warning" icon={<Inventory2 fontSize="small" />} sx={{ mb: 1.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              {filterSelectionMode
                ? `All ${totalCount} contacts ${describeActiveFilters()} will be archived`
                : `${selectionCount} selected contact${selectionCount === 1 ? '' : 's'} will be archived`}
            </Typography>
            <Typography variant="body2">
              Archived contacts are excluded from campaign sends. This is reversible — set Status to
              "Archived" to find them again and reactivate.
            </Typography>
          </Alert>
          {filterSelectionMode && (
            <Alert severity="info" sx={{ mb: 1.5 }}>
              This runs on <strong>every contact matching the filter</strong>, not just the rows on
              this page.
            </Alert>
          )}
          <Typography variant="caption" color="text.secondary">
            Recorded in the activity log as {authUser?.firstName || 'you'} — visible under "Activity
            log" above.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkArchiveConfirmOpen(false)} disabled={bulkSaving}>
            Cancel
          </Button>
          <Button onClick={handleBulkArchive} variant="contained" disabled={bulkSaving}>
            {bulkSaving ? <CircularProgress size={18} /> : 'Archive'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Bulk delete confirm ───────────────────────────────────────────── */}
      <Dialog
        open={bulkDeleteConfirmOpen}
        onClose={() => {
          if (bulkSaving) return;
          setBulkDeleteConfirmOpen(false);
          setBulkDeleteConfirmText('');
        }}
      >
        <DialogTitle>Delete Contacts</DialogTitle>
        <DialogContent>
          {bulkActionError && (
            <Alert severity="error" sx={{ mb: 1.5 }}>
              {bulkActionError}
            </Alert>
          )}
          <Alert severity="error" sx={{ mb: 1.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
              {filterSelectionMode
                ? `${totalCount} contact${totalCount === 1 ? '' : 's'} ${describeActiveFilters()} will be permanently deleted`
                : `${selectionCount} selected contact${selectionCount === 1 ? '' : 's'} will be permanently deleted`}
            </Typography>
            <Typography variant="body2" component="div">
              This also deletes their <strong>entire message history</strong>. There is no undo and
              no restore — unlike Archive, nothing is recoverable afterwards.
            </Typography>
          </Alert>
          {filterSelectionMode && (
            <Alert severity="warning" sx={{ mb: 1.5 }}>
              You are deleting <strong>a filtered batch, not a list you picked</strong>. It includes
              every match on every page — {totalCount} contact
              {totalCount === 1 ? '' : 's'} you have not seen.
            </Alert>
          )}
          <Typography variant="caption" color="text.secondary">
            Recorded in the activity log as {authUser?.firstName || 'you'}, with the count and the
            exact filter used.
          </Typography>
          {filterSelectionMode && (
            <TextField
              autoFocus
              size="small"
              fullWidth
              sx={{ mt: 2 }}
              label={`Type DELETE to confirm`}
              value={bulkDeleteConfirmText}
              onChange={(e) => setBulkDeleteConfirmText(e.target.value)}
              helperText="This deletes a filtered batch, not a hand-picked list — typing this confirms you mean all of it."
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setBulkDeleteConfirmOpen(false);
              setBulkDeleteConfirmText('');
            }}
            disabled={bulkSaving}
          >
            Cancel
          </Button>
          <Button
            onClick={handleBulkDelete}
            variant="contained"
            color="error"
            disabled={
              bulkSaving ||
              (filterSelectionMode && bulkDeleteConfirmText.trim().toUpperCase() !== 'DELETE')
            }
          >
            {bulkSaving ? <CircularProgress size={18} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(bulkSuccessMessage)}
        autoHideDuration={4000}
        onClose={() => setBulkSuccessMessage('')}
      >
        <Alert severity="success" onClose={() => setBulkSuccessMessage('')}>
          {bulkSuccessMessage}
        </Alert>
      </Snackbar>

      <Dialog
        open={attributesOpen}
        onClose={() => setAttributesOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Table Columns</DialogTitle>
        <DialogContent>
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  checked={visibleFields.email}
                  onChange={(e) =>
                    setVisibleFields((prev) => ({ ...prev, email: e.target.checked }))
                  }
                />
              }
              label="Email"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={visibleFields.country}
                  onChange={(e) =>
                    setVisibleFields((prev) => ({ ...prev, country: e.target.checked }))
                  }
                />
              }
              label="Country"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={visibleFields.tags}
                  onChange={(e) =>
                    setVisibleFields((prev) => ({ ...prev, tags: e.target.checked }))
                  }
                />
              }
              label="Tags"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={visibleFields.source}
                  onChange={(e) =>
                    setVisibleFields((prev) => ({ ...prev, source: e.target.checked }))
                  }
                />
              }
              label="Source"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={visibleFields.customFields}
                  onChange={(e) =>
                    setVisibleFields((prev) => ({ ...prev, customFields: e.target.checked }))
                  }
                />
              }
              label="Custom Fields"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={visibleFields.conversation}
                  onChange={(e) =>
                    setVisibleFields((prev) => ({ ...prev, conversation: e.target.checked }))
                  }
                />
              }
              label="Conversation Preview"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={visibleFields.date}
                  onChange={(e) =>
                    setVisibleFields((prev) => ({ ...prev, date: e.target.checked }))
                  }
                />
              }
              label="Date"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={visibleFields.lastNote}
                  onChange={(e) =>
                    setVisibleFields((prev) => ({ ...prev, lastNote: e.target.checked }))
                  }
                />
              }
              label="Last Note"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={visibleFields.status}
                  onChange={(e) =>
                    setVisibleFields((prev) => ({ ...prev, status: e.target.checked }))
                  }
                />
              }
              label="Status"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={visibleFields.owner}
                  onChange={(e) =>
                    setVisibleFields((prev) => ({ ...prev, owner: e.target.checked }))
                  }
                />
              }
              label="Assigned To"
            />
          </FormGroup>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAttributesOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveAttributes} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Add / Edit Contact ──────────────────────────────────────────────── */}
      <Dialog
        open={addOpen}
        onClose={() => !addSaving && setAddOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{editingContactId ? 'Edit Contact' : 'Add Contact'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            {addError && <Alert severity="error">{addError}</Alert>}
            <TextField
              label="Name"
              size="small"
              fullWidth
              value={addForm.name}
              onChange={(e) => setAddForm((p) => ({ ...p, name: e.target.value }))}
            />
            <PhoneField
              label="Phone"
              size="small"
              required
              value={addForm.phone}
              onChange={(phone) => setAddForm((p) => ({ ...p, phone }))}
            />
            <TextField
              label="Email"
              size="small"
              fullWidth
              value={addForm.email}
              onChange={(e) => setAddForm((p) => ({ ...p, email: e.target.value }))}
            />
            <TextField
              label="Country code (e.g. IN, US)"
              size="small"
              fullWidth
              value={addForm.countryCode}
              onChange={(e) => setAddForm((p) => ({ ...p, countryCode: e.target.value }))}
            />
            <Autocomplete
              multiple
              freeSolo
              filterSelectedOptions
              size="small"
              options={allTags}
              value={addForm.tags}
              onChange={(_, value) => setAddForm((p) => ({ ...p, tags: cleanTags(value) }))}
              renderTags={(value, getTagProps) =>
                value.map((tag, index) => (
                  <Chip label={tag} size="small" {...getTagProps({ index })} key={tag} />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...(params as any)}
                  label="Tags"
                  placeholder={addForm.tags.length ? '' : 'Type a tag and press Enter…'}
                  helperText="Use tags to target this contact from a campaign's segment filter"
                />
              )}
            />

            {contactFieldDefs.length > 0 && (
              <Grid container spacing={2}>
                <DynamicFields
                  definitions={contactFieldDefs}
                  values={definedValues}
                  onChange={(k, v) => setDefinedValues((p) => ({ ...p, [k]: v }))}
                />
              </Grid>
            )}

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Custom Fields
              </Typography>
              <Stack spacing={1.5}>
                {addForm.customAttributes.map((field, index) => (
                  <Stack key={index} direction="row" spacing={1} alignItems="flex-start">
                    <Autocomplete
                      freeSolo
                      size="small"
                      options={attributeKeys}
                      value={field.key}
                      onChange={(_, value) =>
                        updateCustomAttributeField(index, { key: value || '' })
                      }
                      onInputChange={(_, value, reason) => {
                        if (reason === 'input') updateCustomAttributeField(index, { key: value });
                      }}
                      sx={{ flex: 1 }}
                      renderInput={(params) => (
                        <TextField
                          {...(params as any)}
                          label="Field name"
                          placeholder="e.g. Plan"
                        />
                      )}
                    />
                    <TextField
                      size="small"
                      label="Value"
                      sx={{ flex: 1 }}
                      value={field.value}
                      onChange={(e) => updateCustomAttributeField(index, { value: e.target.value })}
                    />
                    <IconButton
                      size="small"
                      onClick={() => removeCustomAttributeField(index)}
                      sx={{ mt: 0.5 }}
                      aria-label="Remove field"
                    >
                      <Close fontSize="small" />
                    </IconButton>
                  </Stack>
                ))}
                <Button
                  variant="text"
                  size="small"
                  startIcon={<Add />}
                  onClick={addCustomAttributeField}
                  disabled={addForm.customAttributes.length >= MAX_CUSTOM_FIELDS_PER_CONTACT}
                  sx={{ alignSelf: 'flex-start' }}
                >
                  Add Field
                </Button>
              </Stack>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)} disabled={addSaving}>
            Cancel
          </Button>
          <Button onClick={handleSaveContact} variant="contained" disabled={addSaving}>
            {addSaving ? (
              <CircularProgress size={18} />
            ) : editingContactId ? (
              'Save Changes'
            ) : (
              'Add Contact'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Import Contacts from CSV ────────────────────────────────────────── */}
      <Dialog
        open={importOpen}
        onClose={() => !importSaving && setImportOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Import Contacts from CSV or Excel</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              Upload a CSV or Excel (.xlsx) file with columns for name, phone, email, country code,
              and tags. Phone numbers must include the country code (e.g. +919876543210). Existing
              contacts (matched by phone number) will be updated rather than duplicated. Any other
              column can be mapped to a custom field below.
            </Typography>
            <Button
              variant="text"
              size="small"
              onClick={downloadContactsTemplate}
              sx={{ alignSelf: 'flex-start' }}
            >
              Download CSV Template
            </Button>
            <Button variant="outlined" component="label">
              {importFileName || 'Choose CSV or Excel File'}
              <input
                type="file"
                hidden
                accept=".csv,.xlsx,.xls"
                onChange={handleImportFileChange}
              />
            </Button>

            {importSheetNames.length > 1 && (
              <FormControl size="small" sx={{ maxWidth: 320 }}>
                <InputLabel id="import-sheet-label">Sheet</InputLabel>
                <Select
                  labelId="import-sheet-label"
                  label="Sheet"
                  value={importSelectedSheet}
                  onChange={(e) => handleImportSheetChange(e.target.value)}
                >
                  {importSheetNames.map((name) => (
                    <MenuItem key={name} value={name}>
                      {name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {importError && <Alert severity="error">{importError}</Alert>}

            {importRows.length > 0 && !importResult && (
              <>
                <Alert severity="info">
                  Found {importRows.length} row{importRows.length === 1 ? '' : 's'}. Review the
                  column mapping below before importing — unmapped columns are skipped.
                </Alert>
                <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                  <strong>Multiple columns → Name</strong>: values are joined with a space (e.g.
                  First + Middle + Last → full name). <strong>Multiple columns → Phone</strong>, or
                  a single cell with <strong>/</strong>-separated numbers: each phone creates its
                  own contact, all sharing the same name, email, and other details. Every phone must
                  include its country code (e.g. +91…); spaces and dashes are ignored.
                </Typography>
                <Autocomplete
                  multiple
                  freeSolo
                  filterSelectedOptions
                  size="small"
                  options={allTags}
                  value={importTags}
                  onChange={(_, value) => setImportTags(cleanTags(value))}
                  renderTags={(value, getTagProps) =>
                    value.map((tag, index) => (
                      <Chip label={tag} size="small" {...getTagProps({ index })} key={tag} />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...(params as any)}
                      label="Tags for this import (optional)"
                      placeholder={importTags.length ? '' : 'Type a tag and press Enter…'}
                      helperText="Applied to every contact in this file, in addition to any tags from a mapped CSV column — handy for filtering later by which import they came from"
                    />
                  )}
                />
                <FormControl size="small" sx={{ maxWidth: 320 }}>
                  <InputLabel id="import-default-country-label">
                    Country for phone numbers missing one
                  </InputLabel>
                  <Select
                    labelId="import-default-country-label"
                    label="Country for phone numbers missing one"
                    value={importDefaultCountry}
                    onChange={(e) => setImportDefaultCountry(e.target.value)}
                  >
                    {IMPORT_DEFAULT_COUNTRY_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* ── Ownership ──────────────────────────────────────
                    One choice, not three switches: the mode IS the priority
                    declaration, so "who wins" can never be ambiguous. A select
                    rather than a radio list so this stays one row tall next to
                    the other import controls — the options are mutually
                    exclusive and self-describing, and the resolved order is
                    spelled out underneath anyway. Hidden entirely from anyone
                    who cannot assign, in which case the import keeps its
                    historical 'rules' behaviour. */}
                {canAssignContacts && (
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
                      <FormControl size="small" sx={{ minWidth: 260 }}>
                        <InputLabel id="import-assign-mode-label">Owner for these contacts</InputLabel>
                        <Select
                          labelId="import-assign-mode-label"
                          label="Owner for these contacts"
                          value={importAssignMode}
                          onChange={(e) =>
                            setImportAssignMode(e.target.value as ContactImportAssignment['mode'])
                          }
                        >
                          <MenuItem value="rules">Assignment rules decide</MenuItem>
                          <MenuItem value="column">Owner column in my file</MenuItem>
                          <MenuItem value="single">One person for the whole file</MenuItem>
                          <MenuItem value="none">Nobody — import unassigned</MenuItem>
                        </Select>
                      </FormControl>

                      {importAssignMode === 'single' && (
                        <FormControl size="small" sx={{ minWidth: 220 }}>
                          <InputLabel id="import-assign-user-label">Assign to</InputLabel>
                          <Select
                            labelId="import-assign-user-label"
                            label="Assign to"
                            value={importAssignUserId}
                            onChange={(e) => setImportAssignUserId(e.target.value)}
                          >
                            {teamMembers.map((m) => (
                              <MenuItem key={m._id} value={m._id}>
                                {`${m.firstName || ''} ${m.lastName || ''}`.trim() || m.email}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                    </Stack>

                    {(importAssignMode === 'column' || importAssignMode === 'single') && (
                      <FormGroup>
                        {importAssignMode === 'column' && (
                          <FormControlLabel
                            control={
                              <Checkbox
                                size="small"
                                checked={importAssignFallbackToRules}
                                onChange={(e) => setImportAssignFallbackToRules(e.target.checked)}
                              />
                            }
                            label={
                              <Typography variant="body2">
                                Use assignment rules for rows with no owner
                              </Typography>
                            }
                          />
                        )}
                        {canOverwriteOwnerOnImport && (
                          <FormControlLabel
                            control={
                              <Checkbox
                                size="small"
                                checked={importAssignOverwriteOwner}
                                onChange={(e) => setImportAssignOverwriteOwner(e.target.checked)}
                              />
                            }
                            label={
                              <Typography variant="body2">
                                Replace the owner on contacts that already have one
                              </Typography>
                            }
                          />
                        )}
                      </FormGroup>
                    )}

                    {/* The resolved priority, in words. An admin should not have
                        to infer who wins from the controls above — this line is
                        the reason the mode is a single choice rather than a set
                        of independent flags. */}
                    <Typography variant="caption" color="text.secondary">
                      <strong>Order:</strong> {importOwnerPrecedence}
                    </Typography>

                    {importAssignMode === 'column' && !importHasOwnerColumn && (
                      <Alert severity="warning">
                        Map a column to <strong>Assigned to (owner email)</strong> below.
                      </Alert>
                    )}
                    {importAssignMode === 'column' && importUnknownOwnerEmails.length > 0 && (
                      <Alert severity="warning">
                        {importUnknownOwnerEmails.length} owner address
                        {importUnknownOwnerEmails.length === 1 ? '' : 'es'} match no active member
                        ({importUnknownOwnerEmails.slice(0, 3).join(', ')}
                        {importUnknownOwnerEmails.length > 3
                          ? ` +${importUnknownOwnerEmails.length - 3}`
                          : ''}
                        ). Those contacts import without an owner.
                      </Alert>
                    )}
                  </Stack>
                )}

                {importHasNoteColumn && (
                  <Alert severity="info">
                    Notes are marked <strong>Imported</strong>, added once even if you re-import,
                    and don’t mark contacts as Contacted.
                  </Alert>
                )}
                <TableContainer
                  sx={{ maxHeight: 280, border: '1px solid #e2e8f0', borderRadius: 1 }}
                >
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>CSV Column</TableCell>
                        <TableCell>Maps To</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {importHeaders.map((header) => {
                        const selectValue = getImportFieldSelectValue(header);
                        return (
                          <TableRow key={header}>
                            <TableCell>{header}</TableCell>
                            <TableCell>
                              <Stack direction="row" spacing={1} alignItems="center">
                                <Select
                                  size="small"
                                  value={selectValue}
                                  onChange={(e) => setImportFieldMapping(header, e.target.value)}
                                  sx={{ minWidth: 160 }}
                                >
                                  {IMPORT_TARGET_FIELD_OPTIONS.map((opt) => (
                                    <MenuItem key={opt.value || 'none'} value={opt.value}>
                                      {opt.label}
                                    </MenuItem>
                                  ))}
                                </Select>
                                {selectValue === CUSTOM_FIELD_SELECT_VALUE && (
                                  <Autocomplete
                                    freeSolo
                                    size="small"
                                    options={attributeKeys}
                                    value={(importMapping[header] || '').slice(
                                      CUSTOM_FIELD_MAPPING_PREFIX.length
                                    )}
                                    onChange={(_, value) =>
                                      setImportCustomFieldKey(header, value || '')
                                    }
                                    onInputChange={(_, value, reason) => {
                                      if (reason === 'input')
                                        setImportCustomFieldKey(header, value);
                                    }}
                                    sx={{ minWidth: 180 }}
                                    renderInput={(params) => (
                                      <TextField {...(params as any)} placeholder="Field name" />
                                    )}
                                  />
                                )}
                              </Stack>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}

            {importResult && (
              <Alert
                severity={
                  importResult.dryRun
                    ? importResult.updated > 0
                      ? 'warning'
                      : 'info'
                    : importResult.skipped > 0
                      ? 'warning'
                      : 'success'
                }
              >
                {importResult.dryRun ? (
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Out of {importResult.total} row{importResult.total === 1 ? '' : 's'}:{' '}
                    {importResult.created} {importResult.created === 1 ? 'is a' : 'are'} new contact
                    {importResult.created === 1 ? '' : 's'}, {importResult.updated} already exist
                    {importResult.updated === 1 ? 's' : ''} and will be updated
                    {importResult.skipped > 0
                      ? `, ${importResult.skipped} will be skipped (invalid phone number)`
                      : ''}
                    .
                  </Typography>
                ) : (
                  <>
                    Processed {importResult.total} row{importResult.total === 1 ? '' : 's'}:{' '}
                    {importResult.created} new contact{importResult.created === 1 ? '' : 's'},{' '}
                    {importResult.updated} updated
                    {importResult.skipped > 0 ? `, ${importResult.skipped} skipped` : ''}.
                  </>
                )}
                {importResult.dryRun && importResult.updated > 0 && (
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {importResult.updated} of these {importResult.updated === 1 ? 'is' : 'are'}{' '}
                    already in your contacts (matched by phone number). If you've imported this file
                    before, check before continuing.
                  </Typography>
                )}
                {/* Ownership + notes outcome. Every counter is omitted by the
                    backend when it would be zero, so an import that used
                    neither feature renders nothing extra at all. */}
                {(importResult.assigned ||
                  importResult.assignAlreadyCorrect ||
                  importResult.notesAdded ||
                  importResult.notesSkippedDuplicate) && (
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {importResult.assigned
                      ? `${importResult.assigned} contact${importResult.assigned === 1 ? '' : 's'} ${
                          importResult.dryRun ? 'will be' : 'were'
                        } assigned. `
                      : ''}
                    {importResult.assignAlreadyCorrect
                      ? `${importResult.assignAlreadyCorrect} already had the owner your file names (left untouched). `
                      : ''}
                    {importResult.notesAdded
                      ? `${importResult.notesAdded} note${importResult.notesAdded === 1 ? '' : 's'} ${
                          importResult.dryRun ? 'will be' : 'were'
                        } added. `
                      : ''}
                    {importResult.notesSkippedDuplicate
                      ? `${importResult.notesSkippedDuplicate} note${
                          importResult.notesSkippedDuplicate === 1 ? '' : 's'
                        } skipped — already imported earlier.`
                      : ''}
                  </Typography>
                )}
                {/* The clash the whole precedence model exists for. Shown as a
                    warning rather than an error: these contacts import either
                    way, the question is only who ends up owning them. */}
                {importResult.ruleConflicts?.length ? (
                  <Box sx={{ mt: 1 }}>
                    {importResult.ruleConflicts.map((c) => (
                      <Typography key={`${c.ruleId}-${c.fileTargetLabel}`} variant="body2" sx={{ mt: 0.5 }}>
                        ⚠️ <strong>{c.rowCount}</strong> row{c.rowCount === 1 ? '' : 's'}: an
                        assignment rule would pick <strong>{c.ruleTargetLabel}</strong>, but your
                        file says <strong>{c.fileTargetLabel}</strong> — with your current settings,{' '}
                        <strong>{c.fileTargetLabel}</strong> wins.
                        {c.sampleRows.length > 0 ? ` (e.g. row ${c.sampleRows.join(', ')})` : ''}
                      </Typography>
                    ))}
                  </Box>
                ) : null}
                {importResult.assignSkippedExistingOwner ? (
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    ⚠️ <strong>{importResult.assignSkippedExistingOwner}</strong> contact
                    {importResult.assignSkippedExistingOwner === 1 ? '' : 's'} already belong
                    {importResult.assignSkippedExistingOwner === 1 ? 's' : ''} to someone else and{' '}
                    {importResult.dryRun ? 'will keep' : 'kept'} their current owner. Tick “Replace
                    the owner on contacts that already have one” to change that.
                  </Typography>
                ) : null}
                {importResult.unresolvedOwners?.length ? (
                  <Box sx={{ mt: 1 }}>
                    {importResult.unresolvedOwners.map((o) => (
                      <Typography key={o.email} variant="body2" sx={{ mt: 0.5 }}>
                        ⚠️ <strong>{o.email}</strong> —{' '}
                        {o.reason === 'not_a_user'
                          ? 'no active member of this workspace has that address'
                          : 'that member does not have Full access to Chat Logs, so they cannot own contacts'}
                        . {o.rowCount} row{o.rowCount === 1 ? '' : 's'}{' '}
                        {importResult.dryRun ? 'will import' : 'imported'} without an owner.
                        {o.sampleRows.length > 0 ? ` (e.g. row ${o.sampleRows.join(', ')})` : ''}
                      </Typography>
                    ))}
                  </Box>
                ) : null}
                {importResult.errors.length > 0 && (
                  <Box
                    component="ul"
                    sx={{
                      mt: 1,
                      mb: 0,
                      pl: 2,
                      maxHeight: 160,
                      overflowY: 'auto',
                      border: '1px solid rgba(0,0,0,0.08)',
                      borderRadius: 1,
                      py: 0.5,
                    }}
                  >
                    {importResult.errors.map((err, i) => (
                      <li key={i}>
                        <Typography variant="caption">
                          Row {err.row}: {err.reason}
                        </Typography>
                      </li>
                    ))}
                  </Box>
                )}
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          {importResult?.dryRun && (
            <Button
              onClick={() => setImportResult(null)}
              disabled={importSaving}
              sx={{ mr: 'auto' }}
            >
              Back
            </Button>
          )}
          <Button onClick={() => setImportOpen(false)} disabled={importSaving}>
            {importResult && !importResult.dryRun ? 'Close' : 'Cancel'}
          </Button>
          {!importResult && (
            <Button
              onClick={handlePreviewImportContacts}
              variant="contained"
              disabled={
                importSaving ||
                importRows.length === 0 ||
                !Object.values(importMapping).includes('phone') ||
                importAssignmentIncomplete
              }
            >
              {importSaving ? <CircularProgress size={18} /> : 'Next'}
            </Button>
          )}
          {importResult?.dryRun && (
            <Button
              onClick={handleConfirmImportContacts}
              variant="contained"
              disabled={importSaving}
            >
              {importSaving ? <CircularProgress size={18} /> : 'Continue'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* ── Saved dynamic segments (Tier 3) ─────────────────────────────────── */}
      <Menu
        anchorEl={segmentsMenuAnchor}
        open={Boolean(segmentsMenuAnchor)}
        onClose={() => setSegmentsMenuAnchor(null)}
      >
        <MenuItem onClick={openSaveSegmentDialog}>
          <ListItemIcon>
            <Add fontSize="small" />
          </ListItemIcon>
          <ListItemText>Save current filters as segment…</ListItemText>
        </MenuItem>
        <Divider />
        {savedSegments.length === 0 ? (
          <MenuItem disabled>No saved segments yet</MenuItem>
        ) : (
          savedSegments.map((seg) => (
            <MenuItem
              key={seg._id}
              onClick={() => applySegment(seg)}
              sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 260 }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" noWrap>
                  {seg.name}
                </Typography>
                {seg.description && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    noWrap
                    sx={{ display: 'block' }}
                  >
                    {seg.description}
                  </Typography>
                )}
              </Box>
              <IconButton
                size="small"
                aria-label={`Edit segment "${seg.name}"`}
                onClick={(e) => {
                  e.stopPropagation();
                  openEditSegmentDialog(seg);
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                aria-label={`Delete segment "${seg.name}"`}
                onClick={(e) => {
                  e.stopPropagation();
                  setSegmentDeleteTarget(seg);
                  setSegmentsMenuAnchor(null);
                }}
              >
                <DeleteOutline fontSize="small" />
              </IconButton>
            </MenuItem>
          ))
        )}
      </Menu>

      <Dialog
        open={saveSegmentOpen}
        onClose={() => setSaveSegmentOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Save current filters as segment</DialogTitle>
        <DialogContent>
          {saveSegmentError && (
            <Alert severity="error" sx={{ mb: 1.5 }}>
              {saveSegmentError}
            </Alert>
          )}
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              autoFocus
              label="Name"
              fullWidth
              value={saveSegmentName}
              onChange={(e) => setSaveSegmentName(e.target.value)}
            />
            <TextField
              label="Description (optional)"
              fullWidth
              multiline
              minRows={2}
              value={saveSegmentDescription}
              onChange={(e) => setSaveSegmentDescription(e.target.value)}
            />
            <Typography variant="caption" color="text.secondary">
              Saves the current status, tags, advanced filter, and "More filters" combination as a
              reusable, live-updating segment. Free-text search isn't included — a segment is a
              structural definition, not "everyone matching a search term".
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveSegmentOpen(false)} disabled={saveSegmentSaving}>
            Cancel
          </Button>
          <Button onClick={handleSaveSegment} variant="contained" disabled={saveSegmentSaving}>
            {saveSegmentSaving ? <CircularProgress size={18} /> : 'Save segment'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(editSegmentTarget)}
        onClose={() => setEditSegmentTarget(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Edit Segment</DialogTitle>
        <DialogContent>
          {editSegmentError && (
            <Alert severity="error" sx={{ mb: 1.5 }}>
              {editSegmentError}
            </Alert>
          )}
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              autoFocus
              label="Name"
              fullWidth
              value={editSegmentName}
              onChange={(e) => setEditSegmentName(e.target.value)}
            />
            <TextField
              label="Description (optional)"
              fullWidth
              multiline
              minRows={2}
              value={editSegmentDescription}
              onChange={(e) => setEditSegmentDescription(e.target.value)}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={editSegmentUpdateFilters}
                  onChange={(e) => setEditSegmentUpdateFilters(e.target.checked)}
                />
              }
              label="Also update the filters to match what's currently applied on this page"
            />
            <Typography variant="caption" color="text.secondary">
              {editSegmentUpdateFilters
                ? 'This replaces the segment\'s saved filter definition with the status, tags, advanced filter, and "More filters" combination currently applied — not whatever this segment previously matched.'
                : 'Only the name and description change — the segment keeps filtering exactly what it did before.'}
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditSegmentTarget(null)} disabled={editSegmentSaving}>
            Cancel
          </Button>
          <Button onClick={handleUpdateSegment} variant="contained" disabled={editSegmentSaving}>
            {editSegmentSaving ? <CircularProgress size={18} /> : 'Save changes'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(segmentDeleteTarget)}
        onClose={() => setSegmentDeleteTarget(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Segment</DialogTitle>
        <DialogContent>
          {segmentActionError && (
            <Alert severity="error" sx={{ mb: 1.5 }}>
              {segmentActionError}
            </Alert>
          )}
          <Typography variant="body2">
            Permanently delete the saved segment <strong>{segmentDeleteTarget?.name}</strong>? This
            only removes the saved filter definition — it never touches any contacts.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSegmentDeleteTarget(null)}>Cancel</Button>
          <Button onClick={handleDeleteSegment} variant="contained" color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Bulk assign dialog ───────────────────────────────────────────────── */}
      <Dialog
        open={bulkAssignOpen}
        onClose={() => !bulkAssignSaving && setBulkAssignOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Assign to…</DialogTitle>
        <DialogContent>
          {bulkActionError && (
            <Alert severity="error" sx={{ mb: 1.5 }}>
              {bulkActionError}
            </Alert>
          )}
          <Typography variant="body2" sx={{ mb: 2 }}>
            Assign{' '}
            <strong>
              {selectedIds.size} selected contact{selectedIds.size === 1 ? '' : 's'}
            </strong>{' '}
            to a team member.
          </Typography>
          <FormControl fullWidth size="small">
            <InputLabel id="bulk-assign-user-label">Team member</InputLabel>
            <Select
              labelId="bulk-assign-user-label"
              label="Team member"
              value={bulkAssignUserId}
              onChange={(e) => setBulkAssignUserId(e.target.value)}
            >
              {teamMembers.map((m) => (
                <MenuItem key={m._id} value={m._id}>
                  {`${m.firstName} ${m.lastName}`.trim() || m.email}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkAssignOpen(false)} disabled={bulkAssignSaving}>
            Cancel
          </Button>
          <Button
            onClick={handleBulkAssign}
            variant="contained"
            disabled={bulkAssignSaving || !bulkAssignUserId}
          >
            {bulkAssignSaving ? <CircularProgress size={18} /> : 'Assign'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Unassign confirm dialog (bulk + single row) ─────────────────────── */}
      <Dialog
        open={unassignPendingIds.length > 0}
        onClose={() => !unassignSaving && setUnassignPendingIds([])}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Unassign Contacts</DialogTitle>
        <DialogContent>
          {bulkActionError && (
            <Alert severity="error" sx={{ mb: 1.5 }}>
              {bulkActionError}
            </Alert>
          )}
          <Typography variant="body2" sx={{ mb: 2 }}>
            Remove the current assignment from{' '}
            <strong>
              {unassignPendingIds.length} contact{unassignPendingIds.length === 1 ? '' : 's'}
            </strong>
            ?
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={unassignApplyRules}
                onChange={(e) => setUnassignApplyRules(e.target.checked)}
                disabled={unassignSaving}
              />
            }
            label="Re-apply assignment rules to freed contacts"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUnassignPendingIds([])} disabled={unassignSaving}>
            Cancel
          </Button>
          <Button onClick={handleUnassign} variant="contained" disabled={unassignSaving}>
            {unassignSaving ? <CircularProgress size={18} /> : 'Unassign'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Owner-only: bulk reassign BY FILTER ("select all N matching") ────── */}
      <Dialog
        open={ownerBulkAssignOpen}
        onClose={() => !ownerBulkAssignSaving && setOwnerBulkAssignOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Bulk reassign by filter</DialogTitle>
        <DialogContent>
          {bulkActionError && (
            <Alert severity="error" sx={{ mb: 1.5 }}>
              {bulkActionError}
            </Alert>
          )}
          <Alert severity="warning" sx={{ mb: 2 }}>
            This will assign <strong>all {totalCount}</strong> contacts {describeActiveFilters()} to
            the team member below —{' '}
            <strong>including contacts already assigned to someone else</strong>, whose current
            assignment will be overwritten. This cannot be undone automatically.
          </Alert>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="owner-bulk-assign-user-label">Team member</InputLabel>
            <Select
              labelId="owner-bulk-assign-user-label"
              label="Team member"
              value={ownerBulkAssignUserId}
              onChange={(e) => setOwnerBulkAssignUserId(e.target.value)}
            >
              {teamMembers.map((m) => (
                <MenuItem key={m._id} value={m._id}>
                  {`${m.firstName} ${m.lastName}`.trim() || m.email}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Checkbox
                checked={ownerBulkAssignConsent}
                onChange={(e) => setOwnerBulkAssignConsent(e.target.checked)}
                disabled={ownerBulkAssignSaving}
              />
            }
            label={`I understand this will overwrite the existing assignment on all ${totalCount} matching contacts, regardless of who they're currently assigned to.`}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOwnerBulkAssignOpen(false)} disabled={ownerBulkAssignSaving}>
            Cancel
          </Button>
          <Button
            onClick={handleOwnerBulkAssign}
            variant="contained"
            color="warning"
            disabled={ownerBulkAssignSaving || !ownerBulkAssignUserId || !ownerBulkAssignConsent}
          >
            {ownerBulkAssignSaving ? <CircularProgress size={18} /> : `Reassign all ${totalCount}`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Owner-only: bulk unassign BY FILTER ("select all N matching") ────── */}
      <Dialog
        open={ownerBulkUnassignOpen}
        onClose={() => !ownerBulkUnassignSaving && setOwnerBulkUnassignOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Bulk unassign by filter</DialogTitle>
        <DialogContent>
          {bulkActionError && (
            <Alert severity="error" sx={{ mb: 1.5 }}>
              {bulkActionError}
            </Alert>
          )}
          <Alert severity="warning" sx={{ mb: 2 }}>
            This will remove the current assignment from <strong>every</strong> contact{' '}
            {describeActiveFilters()} that currently has one — up to <strong>{totalCount}</strong>{' '}
            contacts, regardless of who they're assigned to.
          </Alert>
          <FormControlLabel
            control={
              <Checkbox
                checked={ownerBulkUnassignApplyRules}
                onChange={(e) => setOwnerBulkUnassignApplyRules(e.target.checked)}
                disabled={ownerBulkUnassignSaving}
              />
            }
            label="Re-apply assignment rules to freed contacts"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={ownerBulkUnassignConsent}
                onChange={(e) => setOwnerBulkUnassignConsent(e.target.checked)}
                disabled={ownerBulkUnassignSaving}
              />
            }
            label="I understand this will unassign every matching contact, regardless of current owner."
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOwnerBulkUnassignOpen(false)}
            disabled={ownerBulkUnassignSaving}
          >
            Cancel
          </Button>
          <Button
            onClick={handleOwnerBulkUnassign}
            variant="contained"
            color="warning"
            disabled={ownerBulkUnassignSaving || !ownerBulkUnassignConsent}
          >
            {ownerBulkUnassignSaving ? <CircularProgress size={18} /> : 'Unassign all matching'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Quick assign dialog (single row) ────────────────────────────────── */}
      <Dialog
        open={Boolean(quickAssignContact)}
        onClose={() => !quickAssignSaving && setQuickAssignContact(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Assign contact</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Assign{' '}
            <strong>
              {quickAssignContact?.customerName ||
                quickAssignContact?.customerPhone ||
                'this contact'}
            </strong>{' '}
            to:
          </Typography>
          <FormControl fullWidth size="small">
            <InputLabel id="quick-assign-user-label">Team member</InputLabel>
            <Select
              labelId="quick-assign-user-label"
              label="Team member"
              value={quickAssignUserId}
              onChange={(e) => setQuickAssignUserId(e.target.value)}
            >
              {teamMembers.map((m) => (
                <MenuItem key={m._id} value={m._id}>
                  {`${m.firstName} ${m.lastName}`.trim() || m.email}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQuickAssignContact(null)} disabled={quickAssignSaving}>
            Cancel
          </Button>
          <Button
            onClick={handleQuickAssign}
            variant="contained"
            disabled={quickAssignSaving || !quickAssignUserId}
          >
            {quickAssignSaving ? <CircularProgress size={18} /> : 'Assign'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Assignment rules dialog ──────────────────────────────────────────── */}
      <ContactAssignmentRulesDialog
        open={assignRulesOpen}
        onClose={() => setAssignRulesOpen(false)}
        allTags={allTags}
      />
    </Box>
  );
};

export default Contacts;
